import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    CircularProgress,
    Tooltip,
    Snackbar,
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    LinearProgress,
    Checkbox,
    FormControlLabel,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ListItemIcon,
    Tabs,
    Tab,
    Menu,
    Popper,
    ListSubheader,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HistoryIcon from '@mui/icons-material/History';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import HomeIcon from '@mui/icons-material/Home';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TimerIcon from '@mui/icons-material/Timer';
import ScienceIcon from '@mui/icons-material/Science';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StorefrontIcon from '@mui/icons-material/Storefront';
import CheckIcon from '@mui/icons-material/Check';
import openRouterIcon from '../assets/openrouter.jpeg';
import chatGptIcon from '../assets/chatgpt.jpg';
import ollamaIcon from '../assets/ollama.png';
import googleAiIcon from '../assets/googleai.svg';
import groqIcon from '../assets/groq.svg';
import { useNavigate } from 'react-router-dom';
import { firewallService, ollamaService, mlxService, openaiService, googleAiStudioService, openRouterService, groqService, claudeService, llmService } from '../services/api';
import authService from '../services/auth';
import DownloadIcon from '@mui/icons-material/Download';
import SpeedIcon from '@mui/icons-material/Speed';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { formatLocalDateTime } from '../utils/date';

// Ordered sections for FTD-style JSON config (structured document view)
const FTD_SECTION_ORDER = [
    'metadata', 'ntp', 'managementip', 'devicehostname', 'connectivitysetting', 'hafailoverconfiguration',
    'physicalinterface', 'managementconvergencemode', 'staticrouteentrycontainer', 'manualnatrulecontainer',
    'objectnatrulecontainer', 'securityzone', 'dhcpservercontainer', 'dhcpserver', 'dnsservergroup',
    'devicednssettings', 'datadnssettings', 'user', 'aaasetting', 'internalcertificate', 'internalcacertificate',
    'webuicertificate', 'snmpserver', 'devicelogsettings', 'filepolicyconfiguration', 'distinguishedname',
    'distinguishednamegroup', 'networkobject', 'networkobjectgroup', 'accesspolicy', 'accessdefaultaction',
    'intrusionpolicy', 'intrusionsettings', 'networkanalysispolicy', 'identitypolicy', 'sslpolicy',
    'securityintelligencepolicy', 'securityintelligenceurlpolicy', 'securityintelligencenetworkpolicy',
    'securityintelligencednspolicy', 'ravpngrouppolicy', 'ikevtwopolicy', 'ikevonepolicy', 'sslcipher',
    'datasslciphersetting', 'timezonesetting', 'ampcloudconnection', 'externalcacertificategroup',
    'sshaccesslist', 'httpaccesslist', 'connectivitysetting', 'datainterfacehttpsport'
];
const FTD_SECTION_LABELS = {
    metadata: 'Metadata',
    ntp: 'NTP',
    managementip: 'Management IP',
    devicehostname: 'Device hostname',
    connectivitysetting: 'Connectivity',
    hafailoverconfiguration: 'HA failover',
    physicalinterface: 'Interfaces',
    managementconvergencemode: 'Management convergence',
    staticrouteentrycontainer: 'Static routes',
    manualnatrulecontainer: 'NAT',
    objectnatrulecontainer: 'Object NAT',
    securityzone: 'Security zones',
    dhcpservercontainer: 'DHCP',
    dnsservergroup: 'DNS',
    devicednssettings: 'Device DNS',
    datadnssettings: 'Data DNS',
    user: 'Users',
    aaasetting: 'AAA / Access',
    internalcertificate: 'Certificates',
    internalcacertificate: 'Internal CA',
    webuicertificate: 'Web UI certificate',
    snmpserver: 'SNMP',
    devicelogsettings: 'Device logging',
    filepolicyconfiguration: 'File policy',
    distinguishedname: 'Distinguished names',
    distinguishednamegroup: 'Distinguished name groups',
    networkobject: 'Network objects',
    accesspolicy: 'Access policy',
    intrusionpolicy: 'Intrusion policy',
    intrusionsettings: 'Intrusion settings',
    networkanalysispolicy: 'Network analysis',
    identitypolicy: 'Identity policy',
    sslpolicy: 'SSL policy',
    securityintelligencepolicy: 'Security intelligence',
    securityintelligenceurlpolicy: 'Security intelligence URL',
    securityintelligencenetworkpolicy: 'Security intelligence network',
    securityintelligencednspolicy: 'Security intelligence DNS',
    ravpngrouppolicy: 'RA VPN',
    ikevtwopolicy: 'IKE v2 policies',
    ikevonepolicy: 'IKE v1 policies',
    sslcipher: 'SSL ciphers',
    datasslciphersetting: 'Data SSL cipher',
    timezonesetting: 'Timezone',
    ampcloudconnection: 'AMP cloud',
    externalcacertificategroup: 'External CA',
    sshaccesslist: 'SSH access list',
    httpaccesslist: 'HTTP access list',
    datainterfacehttpsport: 'Data interface HTTPS',
};

function parseFtdStructuredConfig(raw) {
    if (!raw || typeof raw !== 'string') return null;
    let jsonStr = raw.trim();
    const headerMatch = jsonStr.match(/^---\s+.+?\s+---\s*\n/s);
    if (headerMatch) jsonStr = jsonStr.slice(headerMatch[0].length).trim();
    if (!jsonStr.startsWith('[')) return null;
    try {
        const arr = JSON.parse(jsonStr);
        if (!Array.isArray(arr)) return null;
        const byType = {};
        arr.forEach((obj) => {
            let typeKey = obj.type === 'metadata' ? 'metadata' : (obj.type === 'identitywrapper' && obj.data && obj.data.type) ? obj.data.type : (obj.type || 'other');
            if (!byType[typeKey]) byType[typeKey] = [];
            byType[typeKey].push(obj);
        });
        const order = FTD_SECTION_ORDER;
        const sections = [];
        const seen = new Set();
        order.forEach((typeKey) => {
            if (byType[typeKey] && byType[typeKey].length > 0) {
                seen.add(typeKey);
                                        sections.push({
                                            id: typeKey,
                                            title: FTD_SECTION_LABELS[typeKey] || (typeKey.charAt(0).toUpperCase() + typeKey.slice(1).replace(/([A-Z])/g, ' $1')),
                                            items: byType[typeKey]
                                        });
                                    }
                                });
                                Object.keys(byType).filter((k) => !seen.has(k)).sort().forEach((typeKey) => {
                                    sections.push({
                                        id: typeKey,
                                        title: FTD_SECTION_LABELS[typeKey] || (typeKey.charAt(0).toUpperCase() + typeKey.slice(1).replace(/([A-Z])/g, ' $1')),
                                        items: byType[typeKey]
                                    });
                                });
                                // Number all sections in display order (1, 2, 3, ...)
                                sections.forEach((sec, idx) => {
                                    sec.numberedTitle = `${idx + 1}. ${sec.title}`;
                                });
        return sections.length ? { sections } : null;
    } catch (_) {
        return null;
    }
}

// Juniper SRX Configuration Section Order and Labels
const JUNIPER_SRX_SECTION_ORDER = [
    'version', 'system', 'security', 'interfaces', 'routing-options', 'protocols', 'firewall', 'policy-options', 'applications', 'groups'
];

const JUNIPER_SRX_SECTION_LABELS = {
    '@': 'Metadata',
    'version': 'Junos Version',
    'system': 'System Configuration',
    'security': 'Security Configuration',
    'interfaces': 'Network Interfaces',
    'routing-options': 'Routing Options',
    'protocols': 'Protocols',
    'firewall': 'Firewall Filters',
    'policy-options': 'Policy Options',
    'applications': 'Applications',
    'groups': 'Configuration Groups'
};

const JUNIPER_SRX_SYSTEM_LABELS = {
    'root-authentication': 'Root Authentication',
    'services': 'Services',
    'syslog': 'System Logging',
    'license': 'License',
    'host-name': 'Hostname',
    'name-server': 'Name Servers',
    'login': 'Login Settings',
    'ntp': 'NTP',
    'time-zone': 'Time Zone'
};

const JUNIPER_SRX_SECURITY_LABELS = {
    'pki': 'PKI / Certificates',
    'screen': 'Screen Options (IDS)',
    'policies': 'Security Policies',
    'zones': 'Security Zones',
    'nat': 'NAT',
    'ike': 'IKE',
    'ipsec': 'IPSec',
    'alg': 'Application Layer Gateway',
    'flow': 'Flow Settings',
    'idp': 'IDP',
    'utm': 'UTM',
    'address-book': 'Address Book'
};

const JUNIPER_SRX_SERVICES_LABELS = {
    'ssh': 'SSH',
    'netconf': 'NETCONF',
    'rest': 'REST API',
    'web-management': 'Web Management',
    'dhcp': 'DHCP',
    'dns': 'DNS'
};

// Parse Juniper SRX JSON configuration into structured sections
function parseJuniperSrxStructuredConfig(raw) {
    if (!raw || typeof raw !== 'string') return null;
    let jsonStr = raw.trim();

    // Handle if wrapped in header markers
    const headerMatch = jsonStr.match(/^---\s+.+?\s+---\s*\n/s);
    if (headerMatch) jsonStr = jsonStr.slice(headerMatch[0].length).trim();

    // Must be a JSON object (starts with {)
    if (!jsonStr.startsWith('{')) return null;

    try {
        const config = JSON.parse(jsonStr);
        if (typeof config !== 'object' || config === null) return null;

        const sections = [];
        const seen = new Set();

        // Process in preferred order first
        JUNIPER_SRX_SECTION_ORDER.forEach((key) => {
            if (config[key] !== undefined) {
                seen.add(key);
                const sectionData = config[key];
                sections.push({
                    id: key,
                    title: JUNIPER_SRX_SECTION_LABELS[key] || formatSectionTitle(key),
                    data: sectionData,
                    subsections: buildJuniperSubsections(key, sectionData)
                });
            }
        });

        // Add any remaining keys not in the order list
        Object.keys(config).filter((k) => !seen.has(k) && k !== '@').sort().forEach((key) => {
            sections.push({
                id: key,
                title: JUNIPER_SRX_SECTION_LABELS[key] || formatSectionTitle(key),
                data: config[key],
                subsections: buildJuniperSubsections(key, config[key])
            });
        });

        // Add metadata (@) at the beginning if present
        if (config['@']) {
            sections.unshift({
                id: '@',
                title: 'Metadata',
                data: config['@'],
                subsections: null
            });
        }

        // Number all sections
        sections.forEach((sec, idx) => {
            sec.numberedTitle = `${idx + 1}. ${sec.title}`;
        });

        return sections.length ? { sections, isJuniperSrx: true } : null;
    } catch (_) {
        return null;
    }
}

// Build subsections for Juniper SRX nested configurations
function buildJuniperSubsections(parentKey, data) {
    if (!data || typeof data !== 'object' || Array.isArray(data)) return null;

    const labelMap = parentKey === 'system' ? JUNIPER_SRX_SYSTEM_LABELS :
                     parentKey === 'security' ? JUNIPER_SRX_SECURITY_LABELS :
                     (parentKey === 'system' && data.services) ? JUNIPER_SRX_SERVICES_LABELS : {};

    const subsections = [];
    Object.keys(data).forEach((key) => {
        const value = data[key];
        if (value !== null && typeof value === 'object') {
            subsections.push({
                id: key,
                title: labelMap[key] || formatSectionTitle(key),
                data: value
            });
        }
    });

    return subsections.length > 0 ? subsections : null;
}

// Format section title from key (kebab-case or camelCase to Title Case)
function formatSectionTitle(key) {
    return key
        .replace(/[-_]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

// PaloAlto PAN-OS Configuration Section Labels
const PALOALTO_SECTION_LABELS = {
    'mgt-config': 'Management Configuration',
    'shared': 'Shared Objects',
    'devices': 'Device Configuration',
    'readonly': 'Read-Only Settings',
};

const PALOALTO_DEVICE_LABELS = {
    'network': 'Network',
    'deviceconfig': 'Device Settings',
    'vsys': 'Virtual Systems',
    'rulebase': 'Rulebase / Policies',
    'setting': 'Settings',
    'log-settings': 'Log Settings',
    'zone': 'Zones',
    'profiles': 'Security Profiles',
    'address': 'Address Objects',
    'address-group': 'Address Groups',
    'service': 'Service Objects',
    'service-group': 'Service Groups',
    'application': 'Applications',
    'application-group': 'Application Groups',
    'schedule': 'Schedules',
    'tag': 'Tags',
};

// Parse PaloAlto PAN-OS XML configuration into structured sections
function parsePaloAltoStructuredConfig(raw) {
    if (!raw || typeof raw !== 'string') return null;
    const trimmed = raw.trim();

    // Must look like XML (starts with <)
    if (!trimmed.startsWith('<')) return null;

    // Check it's a PaloAlto config response
    if (!trimmed.includes('<config') && !trimmed.includes('<response')) return null;

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(trimmed, 'text/xml');
        const parseError = doc.querySelector('parsererror');
        if (parseError) return null;

        // Find the <config> element (might be inside <response><result><config>)
        let configEl = doc.querySelector('config');
        if (!configEl) return null;

        const sections = [];

        // Iterate top-level children of <config>
        Array.from(configEl.children).forEach((child) => {
            const tag = child.tagName;
            const label = PALOALTO_SECTION_LABELS[tag] || formatSectionTitle(tag);

            // Build subsections from child's children
            const subsections = [];
            if (tag === 'devices') {
                // Devices has <entry name="localhost.localdomain"> with actual config inside
                const entries = child.querySelectorAll(':scope > entry');
                entries.forEach((entry) => {
                    Array.from(entry.children).forEach((sub) => {
                        const subTag = sub.tagName;
                        const subLabel = PALOALTO_DEVICE_LABELS[subTag] || formatSectionTitle(subTag);
                        subsections.push({
                            id: subTag,
                            title: subLabel,
                            xml: xmlNodeToIndentedString(sub, 0),
                        });
                    });
                });
            } else {
                Array.from(child.children).forEach((sub) => {
                    const subTag = sub.tagName;
                    subsections.push({
                        id: subTag,
                        title: formatSectionTitle(subTag),
                        xml: xmlNodeToIndentedString(sub, 0),
                    });
                });
            }

            sections.push({
                id: tag,
                title: label,
                xml: xmlNodeToIndentedString(child, 0),
                subsections: subsections.length > 0 ? subsections : null,
            });
        });

        // Number all sections
        sections.forEach((sec, idx) => {
            sec.numberedTitle = `${idx + 1}. ${sec.title}`;
        });

        return sections.length ? { sections, isPaloAlto: true } : null;
    } catch (_) {
        return null;
    }
}

// Convert an XML DOM node to a clean indented string
function xmlNodeToIndentedString(node, depth) {
    const indent = '  '.repeat(depth);
    const children = Array.from(node.childNodes);

    // Text-only node
    if (children.length === 1 && children[0].nodeType === 3) {
        const text = children[0].textContent.trim();
        const attrs = xmlAttrsString(node);
        return `${indent}<${node.tagName}${attrs}>${text}</${node.tagName}>`;
    }

    // No children (self-closing)
    if (children.length === 0) {
        const attrs = xmlAttrsString(node);
        return `${indent}<${node.tagName}${attrs}/>`;
    }

    // Has element children
    const attrs = xmlAttrsString(node);
    let result = `${indent}<${node.tagName}${attrs}>`;
    children.forEach((child) => {
        if (child.nodeType === 1) {
            result += '\n' + xmlNodeToIndentedString(child, depth + 1);
        } else if (child.nodeType === 3 && child.textContent.trim()) {
            result += child.textContent.trim();
        }
    });
    result += `\n${indent}</${node.tagName}>`;
    return result;
}

// Helper to serialize XML attributes
function xmlAttrsString(node) {
    if (!node.attributes || node.attributes.length === 0) return '';
    let s = '';
    for (let i = 0; i < node.attributes.length; i++) {
        const attr = node.attributes[i];
        s += ` ${attr.name}="${attr.value}"`;
    }
    return s;
}

// ── Huawei USG VRP structured config parser ──
// Splits `#`-delimited Huawei VRP config into logical, labelled sections.
const HUAWEI_SECTION_LABELS = {
    'sysname': 'System Name',
    'aaa': 'AAA / Authentication',
    'firewall zone': 'Firewall Zones',
    'interface': 'Interfaces',
    'ip route-static': 'Static Routes',
    'ip vpn-instance': 'VPN Instances',
    'ip address-set': 'IP Address Sets',
    'time-range': 'Time Ranges',
    'snmp-agent': 'SNMP Agent',
    'web-manager': 'Web Manager',
    'firewall dataplane': 'Dataplane Settings',
    'firewall packet-filter': 'Packet Filter',
    'firewall detect': 'Application Detection',
    'firewall defend': 'Attack Defense',
    'log type': 'Logging',
    'update schedule': 'Signature Update Schedule',
    'clock': 'Clock / Timezone',
    'l2tp': 'L2TP Settings',
    'telnet': 'Telnet Settings',
    'ssh': 'SSH Settings',
    'user-interface': 'User Interfaces (VTY/Console)',
    'security-policy': 'Security Policy',
    'traffic-policy': 'Traffic Policy',
    'nat-policy': 'NAT Policy',
    'policy-based-route': 'Policy-Based Routing',
    'dns-transparent-policy': 'DNS Transparent Policy',
    'pcp-policy': 'PCP Policy',
    'isp name': 'ISP Profiles',
    'sa': 'SA',
    'location': 'Location',
    'multi-interface': 'Multi-Interface',
    'right-manager': 'Right Manager',
    'api': 'API / NETCONF',
    'dataflow': 'Dataflow',
};

function parseHuaweiUsgStructuredConfig(raw) {
    if (!raw || typeof raw !== 'string') return null;

    // Quick detection: must contain Huawei VRP markers
    const hasVrpMarker = raw.includes('sysname ') || raw.includes('display current-configuration') ||
        raw.includes('Huawei Technologies') || raw.includes('firewall zone');
    // Must be #-delimited (VRP style), not JSON/XML
    const hasHashDelimiters = (raw.match(/\n#\n/g) || []).length >= 3;
    if (!hasVrpMarker || !hasHashDelimiters) return null;

    try {
        // Clean ANSI escapes and control characters
        let cleaned = raw
            .replace(/\x1b\[[0-9;]*[a-zA-Z]/g, '')
            .replace(/\[42D\s*/g, '')
            .replace(/\r/g, '');

        // Split on lines that are just "#"
        const rawBlocks = cleaned.split(/\n#\n/).map(b => b.trim()).filter(Boolean);

        // Categorise each block into a named section
        const sectionMap = new Map(); // label -> { lines[], key }
        let bannerLines = [];
        let infoLines = [];

        for (const block of rawBlocks) {
            if (!block) continue;

            // Copyright banner
            if (block.includes('Copyright') && block.includes('*')) {
                bannerLines.push(block);
                continue;
            }
            // Info messages
            if (block.startsWith('Info:') || block.match(/^Info:/m)) {
                infoLines.push(block);
                continue;
            }
            // Skip "return" and trailing prompt lines
            if (block === 'return' || block.startsWith('<') || block.startsWith('Software Version')) {
                if (block.startsWith('Software Version') || block.startsWith('!Software')) {
                    bannerLines.push(block);
                }
                continue;
            }

            // Determine section label from first meaningful line
            const firstLine = block.split('\n')[0].trim().replace(/^undo\s+/, '');
            let label = '';
            let key = '';

            // Interface blocks
            if (firstLine.startsWith('interface ')) {
                label = 'Interfaces';
                key = 'interfaces';
            }
            // Firewall zone blocks
            else if (firstLine.startsWith('firewall zone')) {
                label = 'Firewall Zones';
                key = 'firewall-zones';
            }
            // Static routes
            else if (firstLine.startsWith('ip route-static')) {
                label = 'Static Routes';
                key = 'static-routes';
            }
            // User-interface
            else if (firstLine.startsWith('user-interface')) {
                label = 'User Interfaces (VTY/Console)';
                key = 'user-interface';
            }
            // AAA
            else if (firstLine === 'aaa' || firstLine.startsWith('aaa')) {
                label = 'AAA / Authentication';
                key = 'aaa';
            }
            // Manager users (inside aaa but can appear standalone)
            else if (firstLine.startsWith('manager-user')) {
                label = 'AAA / Authentication';
                key = 'aaa';
            }
            // Security policy
            else if (firstLine.startsWith('security-policy')) {
                label = 'Security Policy';
                key = 'security-policy';
            }
            // NAT policy
            else if (firstLine.startsWith('nat-policy')) {
                label = 'NAT Policy';
                key = 'nat-policy';
            }
            // Traffic policy
            else if (firstLine.startsWith('traffic-policy')) {
                label = 'Traffic Policy';
                key = 'traffic-policy';
            }
            // Policy-based route
            else if (firstLine.startsWith('policy-based-route')) {
                label = 'Policy-Based Routing';
                key = 'pbr';
            }
            // SSH
            else if (firstLine.includes('ssh') || firstLine.includes('stelnet')) {
                label = 'SSH Settings';
                key = 'ssh';
            }
            // L2TP
            else if (firstLine.includes('l2tp')) {
                label = 'L2TP Settings';
                key = 'l2tp';
            }
            // ISP names
            else if (firstLine.startsWith('isp name')) {
                label = 'ISP Profiles';
                key = 'isp';
            }
            // SNMP
            else if (firstLine.startsWith('snmp-agent')) {
                label = 'SNMP Agent';
                key = 'snmp';
            }
            // Web manager
            else if (firstLine.startsWith('web-manager')) {
                label = 'Web Manager';
                key = 'web-manager';
            }
            // Firewall dataplane
            else if (firstLine.startsWith('firewall dataplane')) {
                label = 'Dataplane Settings';
                key = 'dataplane';
            }
            // Firewall packet-filter / detect / defend
            else if (firstLine.startsWith('firewall packet-filter')) {
                label = 'Packet Filter';
                key = 'packet-filter';
            } else if (firstLine.startsWith('firewall detect')) {
                label = 'Application Detection';
                key = 'detect';
            } else if (firstLine.startsWith('firewall defend')) {
                label = 'Attack Defense';
                key = 'defend';
            }
            // Log
            else if (firstLine.startsWith('log type')) {
                label = 'Logging';
                key = 'logging';
            }
            // Update schedule
            else if (firstLine.startsWith('update schedule')) {
                label = 'Signature Update Schedule';
                key = 'update-schedule';
            }
            // Clock
            else if (firstLine.startsWith('clock')) {
                label = 'Clock / Timezone';
                key = 'clock';
            }
            // Telnet
            else if (firstLine.includes('telnet')) {
                label = 'Telnet Settings';
                key = 'telnet';
            }
            // Sysname
            else if (firstLine.startsWith('sysname')) {
                label = 'System Name';
                key = 'sysname';
            }
            // IP VPN instance
            else if (firstLine.startsWith('ip vpn-instance')) {
                label = 'VPN Instances';
                key = 'vpn';
            }
            // IP address-set
            else if (firstLine.startsWith('ip address-set')) {
                label = 'IP Address Sets';
                key = 'address-set';
            }
            // Time range
            else if (firstLine.startsWith('time-range')) {
                label = 'Time Ranges';
                key = 'time-range';
            }
            // API / NETCONF
            else if (firstLine.startsWith('api')) {
                label = 'API / NETCONF';
                key = 'api';
            }
            // DNS transparent policy
            else if (firstLine.startsWith('dns-transparent-policy')) {
                label = 'DNS Transparent Policy';
                key = 'dns-transparent';
            }
            // PCP policy
            else if (firstLine.startsWith('pcp-policy')) {
                label = 'PCP Policy';
                key = 'pcp';
            }
            // Dataflow
            else if (firstLine.includes('dataflow')) {
                label = 'Dataflow';
                key = 'dataflow';
            }
            // Multi-interface
            else if (firstLine.startsWith('multi-interface')) {
                label = 'Multi-Interface';
                key = 'multi-interface';
            }
            // Right manager
            else if (firstLine.startsWith('right-manager')) {
                label = 'Right Manager';
                key = 'right-manager';
            }
            // SA
            else if (firstLine === 'sa') {
                label = 'SA';
                key = 'sa';
            }
            // Location
            else if (firstLine === 'location') {
                label = 'Location';
                key = 'location';
            }
            // Role definitions (inside aaa context)
            else if (firstLine.startsWith('role ') || firstLine.startsWith('bind ')) {
                label = 'AAA / Authentication';
                key = 'aaa';
            }
            // Catch-all
            else {
                // Use first keyword as label
                const word = firstLine.split(/\s+/)[0].replace(/^undo$/, '').trim();
                label = word ? formatSectionTitle(word) : 'Other';
                key = word || 'other';
            }

            if (sectionMap.has(key)) {
                sectionMap.get(key).lines.push(block);
            } else {
                sectionMap.set(key, { label, lines: [block] });
            }
        }

        // Build final sections array in a logical order
        const preferredOrder = [
            'sysname', 'clock', 'telnet', 'ssh', 'web-manager',
            'packet-filter', 'detect', 'defend', 'logging', 'dataflow',
            'snmp', 'isp', 'dataplane', 'update-schedule',
            'vpn', 'address-set', 'time-range',
            'aaa',
            'interfaces', 'firewall-zones', 'static-routes',
            'l2tp', 'user-interface',
            'api', 'right-manager', 'multi-interface',
            'security-policy', 'traffic-policy', 'nat-policy', 'pbr',
            'pcp', 'dns-transparent',
            'sa', 'location',
        ];

        const sections = [];
        let idx = 1;

        // Banner + info as first section
        if (bannerLines.length > 0 || infoLines.length > 0) {
            sections.push({
                id: 'banner',
                title: 'Device Banner & Info',
                numberedTitle: `${idx}. Device Banner & Info`,
                content: [...bannerLines, ...infoLines].join('\n'),
                lineCount: [...bannerLines, ...infoLines].join('\n').split('\n').length,
            });
            idx++;
        }

        // Add sections in preferred order
        const added = new Set();
        for (const key of preferredOrder) {
            if (sectionMap.has(key)) {
                const sec = sectionMap.get(key);
                const content = sec.lines.join('\n#\n');
                sections.push({
                    id: key,
                    title: sec.label,
                    numberedTitle: `${idx}. ${sec.label}`,
                    content,
                    lineCount: content.split('\n').length,
                });
                added.add(key);
                idx++;
            }
        }
        // Add any remaining sections not in preferred order
        for (const [key, sec] of sectionMap) {
            if (!added.has(key)) {
                const content = sec.lines.join('\n#\n');
                sections.push({
                    id: key,
                    title: sec.label,
                    numberedTitle: `${idx}. ${sec.label}`,
                    content,
                    lineCount: content.split('\n').length,
                });
                idx++;
            }
        }

        return sections.length > 1 ? { sections, isHuaweiUsg: true } : null;
    } catch (_) {
        return null;
    }
}

// MLX model ids (without the "mlx:" prefix) whose reasoning is an Off/On
// thinking switch rather than gpt-oss levels; the backend decides how On is
// sent for each. Must match the keys of MLX_THINKING_TOGGLE_MODELS in
// BE-NSCAT/utils/mlx_service.py.
const MLX_THINKING_TOGGLE_MODELS = ['local/qwen3.6-35b-a3b-audit', 'qwen3.6-35b-a3b'];

// Ollama model ids with the same Off/On thinking switch, sent as `think`
// false/true. Must match OLLAMA_THINKING_TOGGLE_MODELS in
// BE-NSCAT/utils/ollama_service.py.
const OLLAMA_THINKING_TOGGLE_MODELS = ['qwen3.6:35b-a3b'];

const FirewallList = () => {
    const navigate = useNavigate();
    const isAdmin = authService.isAdmin();

    // Track all active intervals for cleanup
    const activeIntervalsRef = useRef([]);
    const [firewalls, setFirewalls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [selectedFirewall, setSelectedFirewall] = useState(null);
    const [editForm, setEditForm] = useState({
        device_name: '',
        ip_address: '',
        username: '',
        password: '',
        type: '',
        protocol: 'SSH'
    });
    const [editError, setEditError] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [firewallToDelete, setFirewallToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [processingAction, setProcessingAction] = useState(false);
    const [viewConfigOpen, setViewConfigOpen] = useState(false);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [viewingConfig, setViewingConfig] = useState(false);
    const [configSearch, setConfigSearch] = useState('');
    const [configWrapLines, setConfigWrapLines] = useState(true);
    const [configViewMode, setConfigViewMode] = useState('structured'); // 'structured' | 'raw'
    const [selectedType, setSelectedType] = useState('');
    const [selectedDeviceId, setSelectedDeviceId] = useState('');
    const [selectedProtocol, setSelectedProtocol] = useState('');
    const [subnetOctets, setSubnetOctets] = useState(['0', '0', '0', '0']);
    const subnetRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

    const handleOctetChange = (index, value) => {
        const cleaned = value.replace(/[^0-9]/g, '');
        const num = parseInt(cleaned, 10);
        if (cleaned === '' || (num >= 0 && num <= 255)) {
            const newOctets = [...subnetOctets];
            newOctets[index] = cleaned === '' ? '' : String(Math.min(num, 255));
            setSubnetOctets(newOctets);
            // Auto-advance to next octet when 3 digits typed or value >= 26 (can't grow further)
            if (cleaned.length === 3 || (cleaned.length >= 1 && num > 25 && cleaned.length === 2) || (num > 2 && cleaned.length === 1 && index < 3)) {
                // Only auto-advance on 3-digit or when value can't grow to stay ≤255
                if (cleaned.length === 3 && index < 3) {
                    subnetRefs[index + 1].current?.focus();
                }
            }
        }
    };

    const handleOctetKeyDown = (index, e) => {
        if (e.key === '.' || e.key === 'Tab') {
            if (e.key === '.') e.preventDefault();
            if (index < 3) subnetRefs[index + 1].current?.focus();
        }
        if (e.key === 'Backspace' && subnetOctets[index] === '' && index > 0) {
            subnetRefs[index - 1].current?.focus();
        }
    };

    // Cascading Device Type menu state
    const [deviceMenuAnchor, setDeviceMenuAnchor] = useState(null);
    const [deviceSubmenuAnchor, setDeviceSubmenuAnchor] = useState(null);
    const [activeDeviceType, setActiveDeviceType] = useState(null);
    const deviceSubmenuTimeoutRef = useRef(null);

    const deviceTypeDisplayNames = {
        'juniper_srx': 'Juniper SRX',
        'huawei_usg': 'Huawei USG',
        'fortinet_fortigate': 'Fortinet FortiGate',
        'cisco_ftd': 'Cisco FTD',
        'paloalto_panos': 'PaloAlto PAN-OS',
        'checkpoint_security_gateway': 'Checkpoint Security Gateway'
    };

    const groupedDevices = (() => {
        const grouped = {};
        firewalls.forEach((fw) => {
            const type = fw.type || 'other';
            if (!grouped[type]) grouped[type] = [];
            grouped[type].push(fw);
        });
        return grouped;
    })();
    const deviceTypeOrder = ['juniper_srx', 'huawei_usg', 'fortinet_fortigate', 'cisco_ftd', 'paloalto_panos', 'checkpoint_security_gateway'];
    const sortedDeviceTypes = Object.keys(groupedDevices).sort((a, b) => {
        const ia = deviceTypeOrder.indexOf(a);
        const ib = deviceTypeOrder.indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });

    const handleCloseDeviceMenu = () => {
        setDeviceMenuAnchor(null);
        setDeviceSubmenuAnchor(null);
        setActiveDeviceType(null);
        if (deviceSubmenuTimeoutRef.current) clearTimeout(deviceSubmenuTimeoutRef.current);
    };
    const [auditResults, setAuditResults] = useState(null);
    const [viewAuditOpen, setViewAuditOpen] = useState(false);
    const [viewLastAuditOpen, setViewLastAuditOpen] = useState(false);
    const [lastAuditResults, setLastAuditResults] = useState(null);
    const [loadingLastAudit, setLoadingLastAudit] = useState(false);
    const [runningAuditId, setRunningAuditId] = useState(null);
    const [viewStoredConfigOpen, setViewStoredConfigOpen] = useState(false);
    const [storedConfig, setStoredConfig] = useState(null);
    const [loadingStoredConfig, setLoadingStoredConfig] = useState(false);
    const [selectedFirewallForConfig, setSelectedFirewallForConfig] = useState(null);
    const [storedConfigSearch, setStoredConfigSearch] = useState('');
    const [storedConfigWrapLines, setStoredConfigWrapLines] = useState(true);
    const [storedConfigViewMode, setStoredConfigViewMode] = useState('structured'); // 'structured' | 'raw'
    const [auditProgress, setAuditProgress] = useState({});
    const [auditReports, setAuditReports] = useState({});
    const [viewReportsOpen, setViewReportsOpen] = useState(false);
    const [selectedReportsFirewall, setSelectedReportsFirewall] = useState(null);
    const [lastAuditDates, setLastAuditDates] = useState({});
    const [loadingReports, setLoadingReports] = useState(false);
    const [sortOption, setSortOption] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    
    // AI Model selection state
    const [models, setModels] = useState([]);
    const [loadingModels, setLoadingModels] = useState(false);
    const [selectedModel, setSelectedModel] = useState('');
    // gpt-oss reasoning level (Ollama `think` / MLX `reasoning_effort`), offered only for gpt-oss models (see isGptOssSelected).
    const [reasoningLevel, setReasoningLevel] = useState('medium');
    // Thinking switch for models in MLX_THINKING_TOGGLE_MODELS and
    // OLLAMA_THINKING_TOGGLE_MODELS; the backend decides how Off and On are
    // sent (see isThinkingToggleSelected).
    const [thinkingMode, setThinkingMode] = useState('off');
    // Load model button, local models only: 'idle' | 'loading' | 'loaded' | 'error'.
    const [modelLoadState, setModelLoadState] = useState('idle');
    const [modelLoadSeconds, setModelLoadSeconds] = useState(null);
    // Model the button's state belongs to, so a load finishing after the
    // model was changed is ignored.
    const modelLoadTargetRef = useRef(null);
    // True while Run Analysis loads the local model before the audit.
    const [loadingModelForRun, setLoadingModelForRun] = useState(false);
    
    // Online AI Models state (OpenAI)
    const [onlineModels, setOnlineModels] = useState([]);
    const [loadingOnlineModels, setLoadingOnlineModels] = useState(false);
    const [selectedOnlineModel, setSelectedOnlineModel] = useState('');

    // Free Online AI Models state (Google AI Studio)
    const [freeOnlineModels, setFreeOnlineModels] = useState([]);
    const [loadingFreeOnlineModels, setLoadingFreeOnlineModels] = useState(false);
    const [selectedFreeOnlineModel, setSelectedFreeOnlineModel] = useState('');

    // Claude (Anthropic) Models state
    const [claudeModels, setClaudeModels] = useState([]);
    const [loadingClaudeModels, setLoadingClaudeModels] = useState(false);

    // MLX (LM Studio) models, listed in the Local group next to Ollama's.
    // Names carry an "mlx:" prefix, which is how the backend routes them.
    const [mlxModels, setMlxModels] = useState([]);
    const [aiVendors, setAiVendors] = useState(0);

    // Direct configuration analysis state
    const [analyzeConfigOpen, setAnalyzeConfigOpen] = useState(false);
    const [selectedFirewallForAnalysis, setSelectedFirewallForAnalysis] = useState(null);
    const [analysisPrompt, setAnalysisPrompt] = useState('Perform a comprehensive security audit focusing on: 1) Unrestricted access rules and overly permissive policies, 2) Missing deny-all rules and logging configurations, 3) Network segmentation issues and NAT rule vulnerabilities, 4) Compliance with NIST SP 800-53 Rev 5 controls, 5) Rule conflicts and redundancies. For each finding, provide: the specific rule or configuration element, severity level (use exactly one of: Critical, High, Medium, or Low), detailed issue description, NIST SP 800-53 control violation reference, and actionable remediation steps with specific configuration commands.\n\nSeverity Guidelines:\n- Critical: Immediate security risk, allows unauthorized access, data breach risk, or critical compliance violation\n- High: Significant security vulnerability, potential for unauthorized access, or major compliance issue\n- Medium: Moderate security concern, configuration weakness, or minor compliance gap\n- Low: Minor security issue, best practice deviation, or informational finding\n\nNote: The configuration will be analyzed section-by-section (up to 3 sections by default), with each section receiving individual analysis followed by a final correlation pass to identify cross-section issues.');
    const [analysisResult, setAnalysisResult] = useState(null);
    const [loadingAnalysis, setLoadingAnalysis] = useState(false);
    const [skipCorrelation, setSkipCorrelation] = useState(false);  // Option to skip correlation for faster results (false = correlation enabled by default)
    const [useRag, setUseRag] = useState(true);  // NIST RAG Semantic Search (true = enabled by default)
    const [directAuditPollingInterval, setDirectAuditPollingInterval] = useState(null);
    const [availableSections, setAvailableSections] = useState([]);
    const [excludedSections, setExcludedSections] = useState([]);
    const [loadingSections, setLoadingSections] = useState(false);
    const [isPromptEditable, setIsPromptEditable] = useState(false);


    // Fetch sections when dialog opens
    useEffect(() => {
        const fetchSections = async () => {
            if (analyzeConfigOpen && selectedFirewallForAnalysis) {
                setLoadingSections(true);
                try {
                    const result = await firewallService.getAnalyzeConfigSections(selectedFirewallForAnalysis.id);
                    if (result.sections) {
                        setAvailableSections(result.sections);
                        setExcludedSections([]); // Reset excluded sections when fetching new ones
                    }
                } catch (error) {
                    console.error('Error fetching sections:', error);
                    setAvailableSections([]);
                } finally {
                    setLoadingSections(false);
                }
            } else {
                // Reset when dialog closes
                setAvailableSections([]);
                setExcludedSections([]);
            }
        };
        fetchSections();
    }, [analyzeConfigOpen, selectedFirewallForAnalysis]);

    useEffect(() => {
        fetchFirewalls();
        fetchModels();
        fetchOnlineModels();
        fetchFreeOnlineModels();
        fetchClaudeModels();
        fetchMlxModels();
        llmService.getStatus().then(data => {
            setAiVendors(Object.values(data).filter(v => v?.configured).length);
        }).catch(() => setAiVendors(0));
        
        // Cleanup function to stop all polling when component unmounts
        const cleanup = () => {
            console.log('🧹 Cleaning up polling intervals...');
            if (directAuditPollingInterval) {
                clearInterval(directAuditPollingInterval);
                setDirectAuditPollingInterval(null);
                console.log('✅ Cleared directAuditPollingInterval');
            }
            // Clear all tracked intervals
            activeIntervalsRef.current.forEach(id => {
                clearInterval(id);
                clearTimeout(id);
            });
            activeIntervalsRef.current = [];
            console.log('✅ Cleared all tracked intervals');
        };
        
        // Clean up on page unload/refresh
        const handleBeforeUnload = () => {
            cleanup();
        };
        
        // Clean up when visibility changes (tab switch, minimize, etc.)
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // Page is hidden, but don't stop polling yet (user might come back)
                // Just log for debugging
                console.log('📱 Page hidden, polling continues in background');
            }
        };
        
        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            cleanup();
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    
    // Separate effect to clean up polling intervals when they change
    useEffect(() => {
        return () => {
            if (directAuditPollingInterval) {
                console.log('🧹 Cleaning up directAuditPollingInterval on state change');
                clearInterval(directAuditPollingInterval);
            }
        };
    }, [directAuditPollingInterval]);

    const fetchFirewalls = async () => {
        try {
            setLoading(true);
            const data = await firewallService.listFirewalls();
            setFirewalls(data);
            setError(null);
            
            // Fetch last audit dates for each firewall
            data.forEach(firewall => {
                fetchLastAuditDate(firewall.id);
            });
        } catch (err) {
            setError('Failed to fetch firewalls');
            console.error('Error fetching firewalls:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchModels = async () => {
        try {
            setLoadingModels(true);
            const response = await ollamaService.getModels();
            let modelsList = response.models || [];
            // Filter out embedding models (used elsewhere in the app)
            modelsList = modelsList.filter(model => {
                const name = (model.name || '').toLowerCase();
                return !name.includes('embed');
            });
            // Sort models alphabetically by name
            const sortedModels = modelsList.sort((a, b) => {
                const nameA = (a.name || '').toLowerCase();
                const nameB = (b.name || '').toLowerCase();
                return nameA.localeCompare(nameB);
            });
            setModels(sortedModels);
            // No default model selection - user must explicitly select
            
            // Log warning if Ollama timeout occurred (but don't block page)
            if (response.error) {
                console.warn('Ollama service timeout:', response.error);
            }
        } catch (err) {
            console.error('Error fetching models:', err);
            // Set empty models array to allow page to continue loading
            setModels([]);
            // Don't show error to user - models are optional, page should still load
        } finally {
            setLoadingModels(false);
        }
    };

    const fetchOnlineModels = async () => {
        try {
            setLoadingOnlineModels(true);
            const response = await openaiService.getModels();
            setOnlineModels(response.models || []);
            // No default model selection - user must explicitly select
        } catch (err) {
            console.error('Error fetching online models:', err);
            // Don't set error for online models as they're optional
        } finally {
            setLoadingOnlineModels(false);
        }
    };

    const fetchFreeOnlineModels = async () => {
        setLoadingFreeOnlineModels(true);
        try {
            let allModels = [];

            // Fetch OpenRouter free LLMs
            try {
                const openrouterResponse = await openRouterService.getModels();
                allModels = openrouterResponse.models || [];
            } catch (err) {
                console.error('Error fetching OpenRouter free models:', err);
            }

            // Fetch Google AI Studio models
            try {
                const googleResponse = await googleAiStudioService.getModels();
                const googleModels = (googleResponse.models || []).map(model => ({
                    ...model,
                    // Backend already provides the model name with googleaistudio: prefix
                    pricing: { prompt: '0', completion: '0' },
                    context_length: model.context_length || 32000
                }));
                allModels = [...allModels, ...googleModels];
            } catch (err) {
                console.error('Error fetching Google AI Studio models:', err);
            }

            // Fetch Groq models
            try {
                const groqResponse = await groqService.getModels();
                const groqModels = (groqResponse.models || []).map(model => ({
                    ...model,
                    // Backend already provides the model name with groq: prefix
                    pricing: { prompt: '0', completion: '0' },
                    context_length: model.context_length || 128000
                }));
                allModels = [...allModels, ...groqModels];
            } catch (err) {
                console.error('Error fetching Groq models:', err);
            }

            // Sort models: Groq first (with recommended at top), then Google AI Studio, then OpenRouter
            const sortedModels = allModels.sort((a, b) => {
                const aIsGoogle = a.name?.startsWith('googleaistudio:');
                const bIsGoogle = b.name?.startsWith('googleaistudio:');
                const aIsGroq = a.name?.startsWith('groq:');
                const bIsGroq = b.name?.startsWith('groq:');

                // Groq models come first
                if (aIsGroq && !bIsGroq) return -1;
                if (!aIsGroq && bIsGroq) return 1;

                // Within Groq, prioritize recommended models (Llama 4 Scout 17B)
                if (aIsGroq && bIsGroq) {
                    const aRecommended = a.recommended === true;
                    const bRecommended = b.recommended === true;
                    if (aRecommended && !bRecommended) return -1;
                    if (!aRecommended && bRecommended) return 1;
                }

                // Google AI Studio models come second (after Groq)
                if (aIsGoogle && !bIsGoogle && !bIsGroq) return -1;
                if (!aIsGoogle && bIsGoogle && !aIsGroq) return 1;

                // Within Google AI Studio, prioritize Gemini 2.5 Flash-Lite
                if (aIsGoogle && bIsGoogle) {
                    const aIsFlashLite = a.name?.toLowerCase().includes('flash-lite') || a.name?.toLowerCase().includes('2.5-flash-lite');
                    const bIsFlashLite = b.name?.toLowerCase().includes('flash-lite') || b.name?.toLowerCase().includes('2.5-flash-lite');
                    if (aIsFlashLite && !bIsFlashLite) return -1;
                    if (!aIsFlashLite && bIsFlashLite) return 1;
                }

                // Within same group, preserve backend order (recommended first)
                const aRecommended = a.recommended === true;
                const bRecommended = b.recommended === true;
                if (aRecommended && !bRecommended) return -1;
                if (!aRecommended && bRecommended) return 1;
                return 0;
            });

            setFreeOnlineModels(sortedModels);
            // Auto-select Llama 4 Scout 17B (Groq) as default if no model is selected
            if (!selectedModel && !selectedOnlineModel && !selectedFreeOnlineModel) {
                const llama4Scout = sortedModels.find(m => m.name?.includes('llama-4-scout-17b'));
                const defaultModel = llama4Scout;
                if (defaultModel) {
                    setSelectedFreeOnlineModel(defaultModel.name);
                }
            }
        } catch (err) {
            console.error('Error fetching free online models:', err);
        } finally {
            setLoadingFreeOnlineModels(false);
        }
    };

    const fetchMlxModels = async () => {
        const response = await mlxService.getModels();
        setMlxModels(response.models || []);
    };

    const fetchClaudeModels = async () => {
        setLoadingClaudeModels(true);
        try {
            const response = await claudeService.getModels();
            setClaudeModels(response.models || []);
        } catch (err) {
            console.error('Error fetching Claude models:', err);
        } finally {
            setLoadingClaudeModels(false);
        }
    };

    const fetchLastAuditDate = async (firewallId) => {
        try {
            const response = await firewallService.getLatestAuditResults(firewallId);
            if (response && response.audit_date) {
                setLastAuditDates(prev => ({
                    ...prev,
                    [firewallId]: response.audit_date
                }));
            }
        } catch (err) {
            // Don't show error - just means no audit found
            console.log(`No audit found for firewall ${firewallId}`);
        }
    };

    const handleTypeChange = (event) => {
        setSelectedType(event.target.value);
    };

    const handleSortChange = (event) => {
        const value = event.target.value;
        setSortOption(value);

        // Default directions per field for better UX
        if (value === 'name' || value === 'createdAt') {
            // Device name and added date: oldest/A-Z first
            setSortDirection('asc');
        } else if (value === 'recentAudit' || value === 'configUpdated') {
            // Audit and config dates: latest first
            setSortDirection('desc');
        }
    };

    const toggleSortDirection = () => {
        setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    };

    const handleModelChange = (event) => {
        setSelectedModel(event.target.value);
        // Clear online model when offline model is selected
        if (event.target.value) {
            setSelectedOnlineModel('');
            setSelectedFreeOnlineModel('');
        }
    };

    const handleOnlineModelChange = (event) => {
        setSelectedOnlineModel(event.target.value);
        // Clear offline model when online model is selected
        if (event.target.value) {
            setSelectedModel('');
            setSelectedFreeOnlineModel('');
        }
    };

    const handleFreeOnlineModelChange = (event) => {
        setSelectedFreeOnlineModel(event.target.value);
        // Clear other models when free online model is selected
        if (event.target.value) {
            setSelectedModel('');
            setSelectedOnlineModel('');
        }
    };

    // Helper function to get the active model (priority: free online -> OpenAI online -> local)
    const getActiveModel = () => {
        return selectedFreeOnlineModel || selectedOnlineModel || selectedModel;
    };

    // True only when the active model is a local gpt-oss model, on Ollama
    // ("gpt-oss:20b") or MLX ("mlx:openai/gpt-oss-20b"); the Reasoning
    // dropdown and the `reasoning` request field depend on it.
    // True when the active model is a local (MLX or Ollama) model with an
    // Off/On thinking switch instead of reasoning levels.
    const isThinkingToggleSelected = () => {
        const active = getActiveModel();
        if (!active || active !== selectedModel) return false;
        if (active.startsWith('mlx:')) {
            return MLX_THINKING_TOGGLE_MODELS.includes(active.slice('mlx:'.length));
        }
        return OLLAMA_THINKING_TOGGLE_MODELS.includes(active);
    };

    // True when the active model is a local (Ollama or MLX) model; the Load
    // model button and the load step before Run Analysis depend on it.
    const isLocalModelSelected = () => {
        const active = getActiveModel();
        return !!active && active === selectedModel;
    };

    // Show the button as Loaded straight away when the chosen local model is
    // already the only one loaded; any other model change resets it.
    useEffect(() => {
        const active = selectedFreeOnlineModel || selectedOnlineModel || selectedModel;
        const local = analyzeConfigOpen && active && active === selectedModel ? active : null;
        modelLoadTargetRef.current = local;
        setModelLoadState('idle');
        setModelLoadSeconds(null);
        if (!local) return;
        firewallService.getLocalModelStatus(local)
            .then((status) => {
                if (modelLoadTargetRef.current === local && status?.loaded) setModelLoadState('loaded');
            })
            .catch(() => {});
    }, [analyzeConfigOpen, selectedModel, selectedOnlineModel, selectedFreeOnlineModel]);

    const handleLoadModel = async () => {
        const model = getActiveModel();
        setModelLoadState('loading');
        try {
            const result = await firewallService.loadLocalModel(model, 'button');
            if (modelLoadTargetRef.current !== model) return;
            setModelLoadSeconds(result.loaded_now ? result.seconds : null);
            setModelLoadState('loaded');
        } catch (error) {
            if (modelLoadTargetRef.current !== model) return;
            setModelLoadState('error');
            setSnackbar({
                open: true,
                message: `Could not load the model: ${error.response?.data?.detail || error.message}`,
                severity: 'error'
            });
        }
    };

    const isGptOssSelected = () => {
        const active = getActiveModel();
        if (!active || active !== selectedModel) return false;
        return active.startsWith('gpt-oss') || (active.startsWith('mlx:') && active.includes('gpt-oss'));
    };

    const formatModelSize = (size) => {
        if (size >= 1024 * 1024 * 1024) {
            return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
        } else if (size >= 1024 * 1024) {
            return `${(size / (1024 * 1024)).toFixed(1)} MB`;
        } else if (size >= 1024) {
            return `${(size / 1024).toFixed(1)} KB`;
        }
        return `${size} B`;
    };

    // AI Vendor cascading menu state (inline with AIChat)
    const [vendorMenuAnchor, setVendorMenuAnchor] = useState(null);
    const [activeVendor, setActiveVendor] = useState(null);
    const [vendorSubMenuAnchor, setVendorSubMenuAnchor] = useState(null);

    const vendorGroups = [
        {
            key: 'ollama',
            label: 'Local',
            icon: ollamaIcon,
            // Ollama and MLX (LM Studio) models together; the suffix says which
            // runtime serves each one. MLX names keep "mlx:<publisher>/<model>"
            // as the value sent to the backend and show only <model>, e.g.
            // "mlx:openai/gpt-oss-20b" -> "gpt-oss-20b (MLX)".
            models: [
                ...[...models].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(m => ({
                    name: m.name,
                    displayName: `${m.name} (Ollama)`,
                    description: m.size ? formatModelSize(m.size) : 'Local model',
                })),
                ...[...mlxModels].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(m => ({
                    name: m.name,
                    displayName: `${m.name.replace(/^mlx:/, '').split('/').pop()} (MLX)`,
                    description: 'Local model (LM Studio)',
                })),
            ],
            loading: loadingModels,
        },
        {
            key: 'groq',
            label: 'Groq',
            icon: groqIcon,
            models: freeOnlineModels.filter(m => m.name?.startsWith('groq:')).map(m => ({
                name: m.name,
                displayName: m.display_name || m.name,
                description: m.description || 'Groq model',
                recommended: m.recommended,
            })),
            loading: loadingFreeOnlineModels,
        },
        {
            key: 'googleaistudio',
            label: 'Google AI Studio',
            icon: googleAiIcon,
            models: freeOnlineModels.filter(m => m.name?.startsWith('googleaistudio:')).map(m => ({
                name: m.name,
                displayName: m.display_name || m.name,
                description: m.description || 'Google AI model',
                recommended: m.recommended,
            })),
            loading: loadingFreeOnlineModels,
        },
        {
            key: 'openrouter',
            label: 'OpenRouter',
            icon: openRouterIcon,
            models: freeOnlineModels.filter(m => !m.name?.startsWith('groq:') && !m.name?.startsWith('googleaistudio:')).map(m => ({
                name: m.name,
                displayName: m.display_name || m.name,
                description: m.description || 'OpenRouter model',
                recommended: m.recommended,
            })),
            loading: loadingFreeOnlineModels,
        },
        {
            key: 'openai',
            label: 'OpenAI',
            icon: chatGptIcon,
            models: [...onlineModels].sort((a, b) => (a.display_name || a.name || '').localeCompare(b.display_name || b.name || '')).map(m => ({
                name: m.name,
                displayName: m.display_name || m.name,
                description: m.description || 'OpenAI model',
                recommended: m.recommended,
            })),
            loading: loadingOnlineModels,
        },
        {
            key: 'claude',
            label: 'Claude (Anthropic)',
            icon: null,
            muiIcon: 'AutoAwesome',
            models: claudeModels.map(m => ({
                name: m.name,
                displayName: m.display_name || m.name,
                description: m.description || 'Anthropic Claude model',
                recommended: m.recommended,
            })),
            loading: loadingClaudeModels,
        },
    ];

    const handleVendorMenuOpen = (event) => {
        setVendorMenuAnchor(event.currentTarget);
    };

    const handleVendorMenuClose = () => {
        setVendorMenuAnchor(null);
        setActiveVendor(null);
        setVendorSubMenuAnchor(null);
    };

    const handleVendorHover = (event, vendorKey) => {
        setActiveVendor(vendorKey);
        setVendorSubMenuAnchor(event.currentTarget);
    };

    const handleVendorModelSelect = (vendorKey, modelName) => {
        if (vendorKey === 'ollama') {
            setSelectedModel(modelName);
            setSelectedOnlineModel('');
            setSelectedFreeOnlineModel('');
        } else if (vendorKey === 'openai') {
            setSelectedOnlineModel(modelName);
            setSelectedModel('');
            setSelectedFreeOnlineModel('');
        } else if (vendorKey === 'claude') {
            setSelectedFreeOnlineModel(modelName);
            setSelectedModel('');
            setSelectedOnlineModel('');
        } else {
            setSelectedFreeOnlineModel(modelName);
            setSelectedModel('');
            setSelectedOnlineModel('');
        }
        handleVendorMenuClose();
    };

    const getSelectedVendorInfo = () => {
        const activeModel = selectedFreeOnlineModel || selectedOnlineModel || selectedModel;
        if (!activeModel) return null;
        for (const vendor of vendorGroups) {
            const found = vendor.models.find(m => m.name === activeModel);
            if (found) return { vendor, model: found };
        }
        return null;
    };

    const filteredFirewalls = firewalls.filter(firewall => {
        if (selectedDeviceId && firewall.id !== selectedDeviceId) return false;
        if (!selectedDeviceId && selectedType && firewall.type !== selectedType) return false;
        if (selectedProtocol && (firewall.protocol || 'SSH').toUpperCase() !== selectedProtocol) return false;
        if (subnetOctets.some(o => o !== '' && o !== '0')) {
            const ip = (firewall.ip_address || '').split('.');
            for (let i = 0; i < 4; i++) {
                if (subnetOctets[i] !== '' && subnetOctets[i] !== '0' && ip[i] !== subnetOctets[i]) return false;
            }
        }
        return true;
    });

    const sortedFirewalls = [...filteredFirewalls].sort((a, b) => {
        if (sortOption === 'name') {
            const nameA = (a.device_name || '').toLowerCase();
            const nameB = (b.device_name || '').toLowerCase();
            const comparison = nameA.localeCompare(nameB);
            return sortDirection === 'asc' ? comparison : -comparison;
        }

        if (sortOption === 'ip') {
            const partsA = (a.ip_address || '0.0.0.0').split('.').map(Number);
            const partsB = (b.ip_address || '0.0.0.0').split('.').map(Number);
            for (let i = 0; i < 4; i++) {
                if (partsA[i] !== partsB[i]) {
                    const diff = partsA[i] - partsB[i];
                    return sortDirection === 'asc' ? diff : -diff;
                }
            }
            return 0;
        }

        if (sortOption === 'recentAudit') {
            const dateA = lastAuditDates[a.id] ? new Date(lastAuditDates[a.id]) : null;
            const dateB = lastAuditDates[b.id] ? new Date(lastAuditDates[b.id]) : null;

            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;

            const diff = dateA - dateB;
            return sortDirection === 'asc' ? diff : -diff;
        }

        if (sortOption === 'configUpdated') {
            const dateA = a.config_updated_at ? new Date(a.config_updated_at) : null;
            const dateB = b.config_updated_at ? new Date(b.config_updated_at) : null;

            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;

            const diff = dateA - dateB;
            return sortDirection === 'asc' ? diff : -diff;
        }

        if (sortOption === 'createdAt') {
            const dateA = a.created_at ? new Date(a.created_at) : null;
            const dateB = b.created_at ? new Date(b.created_at) : null;

            if (!dateA && !dateB) return 0;
            if (!dateA) return 1;
            if (!dateB) return -1;

            const diff = dateA - dateB;
            return sortDirection === 'asc' ? diff : -diff;
        }

        return 0;
    });

    const handleEditClick = (firewall) => {
        setSelectedFirewall(firewall);
        setEditForm({
            device_name: firewall.device_name,
            ip_address: firewall.ip_address,
            username: '', // Username field is left empty for security
            password: '', // Password field is left empty for security
            type: firewall.type || 'unknown',
            protocol: (firewall.protocol || 'SSH').toUpperCase()
        });
        setEditDialogOpen(true);
        setEditError(null);
    };

    const handleEditSubmit = async () => {
        try {
            setProcessingAction(true);
            setEditError(null);
            // Only include password in update if it's not empty
            const updateData = {
                device_name: editForm.device_name,
                ip_address: editForm.ip_address,
                type: editForm.type,
                protocol: (editForm.type === 'cisco_ftd' || editForm.type === 'juniper_srx' || editForm.type === 'paloalto_panos') ? (editForm.protocol || 'SSH') : 'SSH'
            };
            if (editForm.password) {
                updateData.password = editForm.password;
            }
            if (editForm.username) {
                updateData.username = editForm.username;
            }

            await firewallService.updateFirewall(selectedFirewall.id, updateData);
            await fetchFirewalls(); // Refresh the list
            setEditDialogOpen(false);
            setSnackbar({
                open: true,
                message: 'Firewall updated successfully',
                severity: 'success'
            });
        } catch (err) {
            setEditError(err.response?.data?.error || 'Failed to update firewall');
            console.error('Error updating firewall:', err);
        } finally {
            setProcessingAction(false);
        }
    };

    const handleDeleteClick = (firewall) => {
        setFirewallToDelete(firewall);
        setDeleteConfirmOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!firewallToDelete) return;

        try {
            setProcessingAction(true);
            await firewallService.deleteFirewall(firewallToDelete.id);
            await fetchFirewalls();
            setDeleteConfirmOpen(false);
            setFirewallToDelete(null);
            setSnackbar({
                open: true,
                message: 'Firewall deleted successfully',
                severity: 'success'
            });
        } catch (err) {
            setError('Failed to delete firewall');
            console.error('Error deleting firewall:', err);
            setSnackbar({
                open: true,
                message: 'Failed to delete firewall',
                severity: 'error'
            });
        } finally {
            setProcessingAction(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleViewConfig = async (firewall) => {
        try {
            setViewingConfig(true);
            setSnackbar({
                open: true,
                message: 'Fetching configuration from device...',
                severity: 'info'
            });

            let response;
            
            // Use dedicated endpoints for specific firewall types
            const firewallType = firewall.type?.toLowerCase();
            if (firewallType === 'fortinet_fortigate' || firewallType === 'fortinet' || firewallType === 'fortigate') {
                response = await firewallService.fetchFortinetConfig(firewall.id);
            } else if (firewallType === 'cisco_ftd' || firewallType === 'cisco' || firewallType === 'ftd' || firewallType === 'firepower') {
                response = await firewallService.fetchCiscoFtdConfig(firewall.id);
            } else if (firewallType === 'paloalto_panos' || firewallType === 'paloalto' || firewallType === 'panos' || firewallType === 'pan-os') {
                response = await firewallService.fetchPaloAltoConfig(firewall.id);
            } else if (firewallType === 'checkpoint_security_gateway' || firewallType === 'checkpoint' || firewallType === 'security_gateway' || firewallType === 'cp_sg') {
                response = await firewallService.fetchCheckpointConfig(firewall.id);
            } else {
                // Use generic endpoint for other firewall types
                response = await firewallService.fetchConfig(firewall.id);
            }
            
            if (!response.config) {
                throw new Error('No configuration data received');
            }

            // Store the configuration in state
            setSelectedConfig(response.config);
            setViewConfigOpen(true);

            // Refresh the firewall list to update the timestamp
            await fetchFirewalls();
            
            setSnackbar({
                open: true,
                message: 'Configuration fetched and displayed successfully',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error viewing configuration:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.detail || 'Failed to fetch configuration',
                severity: 'error'
            });
        } finally {
            setViewingConfig(false);
        }
    };

    const handleViewLastAudit = async (firewall) => {
        try {
            setLoadingLastAudit(true);
            setSnackbar({
                open: true,
                message: 'Fetching last audit report...',
                severity: 'info'
            });

            // Get the latest audit report for this firewall
            const reportsData = await firewallService.getAuditReports(firewall.id, 1, 1);
            
            if (!reportsData || !reportsData.reports || reportsData.reports.length === 0) {
                setSnackbar({
                    open: true,
                    message: 'No audit report found for this firewall',
                    severity: 'warning'
                });
                return;
            }

            // Get the first (most recent) report
            const latestReport = reportsData.reports[0];
            
            // Open the PDF report in a new tab
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/audit-reports/${latestReport.id}/download`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const blob = await response.blob();
            
            // Ensure blob has PDF MIME type for proper viewing
            const pdfBlob = blob.type === 'application/pdf' 
                ? blob 
                : new Blob([blob], { type: 'application/pdf' });
            
            // Create blob URL
            const blobUrl = window.URL.createObjectURL(pdfBlob);
            
            // Open PDF in new tab
            const newWindow = window.open(blobUrl, '_blank');
            if (newWindow) {
                newWindow.focus();
                // Clean up the blob URL after a delay (give time for the tab to load)
                setTimeout(() => {
                    window.URL.revokeObjectURL(blobUrl);
                }, 1000);
                setSnackbar({
                    open: true,
                    message: 'Last audit report opened successfully',
                    severity: 'success'
                });
            } else {
                // Fallback: download the file if popup is blocked
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = latestReport.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => {
                    window.URL.revokeObjectURL(blobUrl);
                }, 1000);
                setSnackbar({
                    open: true,
                    message: 'Popup blocked. Report downloaded instead.',
                    severity: 'info'
                });
            }
        } catch (err) {
            console.error('Error fetching last audit report:', err);
            setSnackbar({
                open: true,
                message: err.response?.data?.detail || 'Failed to fetch last audit report',
                severity: 'error'
            });
        } finally {
            setLoadingLastAudit(false);
        }
    };

    const handleViewStoredConfig = async (firewall) => {
        try {
            setLoadingStoredConfig(true);
            setSnackbar({
                open: true,
                message: 'Fetching stored configuration...',
                severity: 'info'
            });

            const response = await firewallService.getStoredConfig(firewall.id);
            setStoredConfig(response);
            setSelectedFirewallForConfig(firewall);
            setViewStoredConfigOpen(true);

            setSnackbar({
                open: true,
                message: 'Stored configuration loaded successfully',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error viewing stored configuration:', err);
            let errorMessage = 'Failed to fetch stored configuration';
            
            if (err.response?.data?.detail) {
                if (err.response.data.detail.includes('No stored configuration found')) {
                    errorMessage = 'No stored configuration found. Please fetch the configuration first using the "Fetch Config" button.';
                } else if (err.response.data.detail.includes('Firewall not found')) {
                    errorMessage = 'Firewall not found.';
                } else {
                    errorMessage = err.response.data.detail;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }
            
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        } finally {
            setLoadingStoredConfig(false);
        }
    };

    const handleDownloadStoredConfig = async (firewall) => {
        try {
            const blob = await firewallService.downloadStoredConfig(firewall.id);
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            
            // Create filename
            const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const safeName = firewall.device_name.replace(/[^a-zA-Z0-9\s\-_]/g, '');
            link.download = `${safeName}_config_${timestamp}.txt`;
            
            // Trigger download
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            setSnackbar({
                open: true,
                message: 'Configuration downloaded successfully',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error downloading configuration:', err);
            setSnackbar({
                open: true,
                message: `Failed to download configuration: ${err.message}`,
                severity: 'error'
            });
        }
    };

    const handleCopyStoredConfig = async () => {
        if (!storedConfig || !storedConfig.config) {
            return;
        }

        try {
            await navigator.clipboard.writeText(storedConfig.config);
            setSnackbar({
                open: true,
                message: 'Stored configuration copied to clipboard',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error copying stored configuration:', err);
            setSnackbar({
                open: true,
                message: 'Failed to copy stored configuration',
                severity: 'error'
            });
        }
    };

    const formatDate = (dateString) => formatLocalDateTime(dateString);

    const handleStopAudit = async (firewallId) => {
        try {
            setProcessingAction(true);
            await firewallService.stopAudit(firewallId);
            setSnackbar({
                open: true,
                message: 'Stop request sent. The audit will stop shortly.',
                severity: 'info'
            });
        } catch (err) {
            setSnackbar({
                open: true,
                message: err.response?.data?.detail || 'Failed to stop audit',
                severity: 'error'
            });
        } finally {
            setProcessingAction(false);
        }
    };

    const handleLoadMoreReports = async (firewallId, nextPage) => {
        try {
            setLoadingReports(true);
            
            const reportsData = await firewallService.getAuditReports(firewallId, nextPage, 5);
            
            setAuditReports(prev => ({
                ...prev,
                [firewallId]: {
                    reports: [...(prev[firewallId]?.reports || []), ...reportsData.reports],
                    pagination: reportsData.pagination,
                    currentPage: nextPage
                }
            }));
        } catch (err) {
            console.error('Error loading more reports:', err);
            setSnackbar({
                open: true,
                message: 'Failed to load more reports',
                severity: 'error'
            });
        } finally {
            setLoadingReports(false);
        }
    };

    const handleOpenReportInNewTab = async (firewallId, auditHistoryId = null) => {
        try {
            // Wait for PDF generation to complete - retry multiple times
            let matchingReport = null;
            const maxRetries = 8; // Increased retries for PDF generation
            const retryDelay = 3000; // 3 seconds between retries (increased for PDF generation)
            
            // Initial wait before first attempt (PDF generation takes time)
            await new Promise(resolve => setTimeout(resolve, 3000));
            
            for (let attempt = 1; attempt <= maxRetries; attempt++) {
                // Wait before checking (except first attempt - already waited above)
                if (attempt > 1) {
                    await new Promise(resolve => setTimeout(resolve, retryDelay));
                }
                
                // Reset matchingReport for each attempt
                matchingReport = null;
                
                // If auditHistoryId is provided, use it directly
                // Otherwise, fetch the latest audit history to get the ID
                let targetAuditId = auditHistoryId;
                
                if (targetAuditId) {
                    console.log(`Using provided audit_history_id: ${targetAuditId} for Configuration Audit`);
                }
                
                if (!targetAuditId) {
                    // Get the latest audit history for this firewall
                    const auditHistory = await firewallService.getAuditHistory({}, 1, 20);
                    // Sort by audit_date descending to get the most recent first
                    const completedAudits = auditHistory.audits?.filter(audit => 
                        audit.firewall_id === firewallId && audit.status === 'completed'
                    ).sort((a, b) => {
                        const dateA = new Date(a.audit_date);
                        const dateB = new Date(b.audit_date);
                        return dateB - dateA; // Most recent first
                    });
                    
                    if (completedAudits && completedAudits.length > 0) {
                        // All audits are direct (configuration audit)
                        targetAuditId = completedAudits[0].id;
                        console.log(`Found latest audit: ID=${targetAuditId}, Date=${completedAudits[0].audit_date}`);
                    } else {
                        if (attempt === maxRetries) {
                            console.log('No completed audit history found for firewall:', firewallId);
                            return;
                        }
                        continue; // Retry
                    }
                }
                
                // Get audit reports for this firewall - check multiple pages if needed
                let page = 1;
                const maxPages = 5; // Check first 5 pages (50 reports) to be more thorough
                
                while (page <= maxPages && !matchingReport) {
                    const reportsResponse = await firewallService.getAuditReports(firewallId, page, 10);
                    
                    if (reportsResponse && reportsResponse.reports && reportsResponse.reports.length > 0) {
                        console.log(`Checking page ${page}, found ${reportsResponse.reports.length} reports, looking for audit_history_id: ${targetAuditId}`);
                        console.log(`Available audit_history_ids:`, reportsResponse.reports.map(r => ({ id: r.id, audit_history_id: r.audit_history_id, filename: r.filename })));
                        
                        // Find the report that matches this specific audit
                        // Convert both to numbers for comparison (in case of type mismatch)
                        matchingReport = reportsResponse.reports.find(report => {
                            const reportAuditId = typeof report.audit_history_id === 'string' 
                                ? parseInt(report.audit_history_id, 10) 
                                : report.audit_history_id;
                            const targetId = typeof targetAuditId === 'string' 
                                ? parseInt(targetAuditId, 10) 
                                : targetAuditId;
                            return reportAuditId === targetId;
                        });
                        
                        if (matchingReport) {
                            console.log('Found matching report:', matchingReport);
                            break;
                        }
                        
                        // Check if there are more pages
                        if (!reportsResponse.pagination?.has_next) break;
                        page++;
                    } else {
                        break;
                    }
                }
                
                if (matchingReport) {
                    await handleOpenReport(matchingReport.id, matchingReport.filename);
                    return; // Success - exit function
                }
                
                // If no report found and this is not the last attempt, continue retrying
                if (attempt < maxRetries) {
                    console.log(`PDF report not found yet, retrying... (attempt ${attempt}/${maxRetries})`);
                    continue;
                }
            }
            
            // No exact match found after all retries - PDF was not generated for this audit
            // DO NOT use fallback to open old reports - this is misleading
            console.log('No matching PDF report found for audit_history_id:', auditHistoryId);
            console.log('PDF report may not have been generated due to Free LLM quota limits or other errors.');
            // Don't show error - the audit response will contain error details if applicable
        } catch (err) {
            console.error('Error opening report in new tab:', err);
            // Don't show error to user - PDF might not be available
        }
    };

    const handleOpenReport = async (reportId, filename) => {
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/audit-reports/${reportId}/download`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const blob = await response.blob();
            
            // Ensure blob has PDF MIME type for proper viewing
            const pdfBlob = blob.type === 'application/pdf' 
                ? blob 
                : new Blob([blob], { type: 'application/pdf' });
            
            // Create blob URL
            const blobUrl = window.URL.createObjectURL(pdfBlob);
            
            // Open PDF in new tab
            const newWindow = window.open(blobUrl, '_blank');
            if (newWindow) {
                newWindow.focus();
                // Clean up the blob URL after a delay (give time for the tab to load)
                setTimeout(() => {
                    window.URL.revokeObjectURL(blobUrl);
                }, 1000);
            } else {
                // Fallback: download the file if popup is blocked
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => {
                    window.URL.revokeObjectURL(blobUrl);
                }, 1000);
                setSnackbar({
                    open: true,
                    message: 'Popup blocked. Report downloaded instead.',
                    severity: 'info'
                });
            }
        } catch (err) {
            console.error('Error opening report:', err);
            // Don't show error - PDF might not be available
        }
    };

    const handleDownloadReport = async (reportId, filename) => {
        try {
            const blob = await firewallService.downloadAuditReport(reportId);
            
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
            setSnackbar({
                open: true,
                message: 'Report downloaded successfully',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error downloading report:', err);
            setSnackbar({
                open: true,
                message: 'Failed to download report',
                severity: 'error'
            });
        }
    };

    const handleDeleteReport = async (reportId, firewallId) => {
        try {
            await firewallService.deleteAuditReport(reportId);
            
            // Remove from state
            setAuditReports(prev => ({
                ...prev,
                [firewallId]: {
                    ...prev[firewallId],
                    reports: prev[firewallId].reports.filter(report => report.id !== reportId),
                    pagination: {
                        ...prev[firewallId].pagination,
                        total_count: prev[firewallId].pagination.total_count - 1
                    }
                }
            }));
            
            setSnackbar({
                open: true,
                message: 'Report deleted successfully',
                severity: 'success'
            });
        } catch (err) {
            console.error('Error deleting report:', err);
            setSnackbar({
                open: true,
                message: 'Failed to delete report',
                severity: 'error'
            });
        }
    };

    const handleAnalyzeConfigDirect = async (firewall) => {
        try {
            setSelectedFirewallForAnalysis(firewall);
            setAnalyzeConfigOpen(true);
        } catch (error) {
            console.error('Error opening configuration analysis:', error);
            setSnackbar({
                open: true,
                message: `Error: ${error.message}`,
                severity: 'error'
            });
        }
    };

    // Polling function for Configuration Audit completion
    const startDirectAuditPolling = async (firewallId, startTime) => {
        // Clear any existing polling interval
        if (directAuditPollingInterval) {
            console.log('🛑 Clearing existing directAuditPollingInterval before starting new one');
            clearInterval(directAuditPollingInterval);
            setDirectAuditPollingInterval(null);
        }
        
        // Also check for any intervals that might be running but not tracked
        // This is a safety measure
        const existingInterval = directAuditPollingInterval;
        if (existingInterval) {
            clearInterval(existingInterval);
        }
        
        let pollCount = 0;
        const maxPolls = 180; // Poll for up to 30 minutes (180 * 10 seconds = 30 minutes)
        const pollInterval = 10000; // Poll every 10 seconds (reduce frequency after initial checks)
        
        const pollForCompletion = async () => {
            try {
                pollCount++;
                const elapsedMinutes = Math.floor((Date.now() - startTime) / 1000 / 60);
                
                // Check for new audit reports - look for direct audit reports
                const reportsData = await firewallService.getAuditReports(firewallId, 1, 10, { polling: true });

                // If request timed out (backend busy with audit), skip this poll cycle
                if (!reportsData) return;

                if (reportsData.reports && reportsData.reports.length > 0) {
                    // Find the latest direct audit report
                    const directReports = reportsData.reports.filter(
                        report => report.report_type === 'direct'
                    );
                    
                    if (directReports.length > 0) {
                        const latestDirectReport = directReports[0];
                        const reportTime = new Date(latestDirectReport.generated_at);
                        const reportTimestamp = reportTime.getTime();
                        
                        // Check if report was generated after we started (with 5 minute buffer to account for clock differences)
                        // Also check if it's within the last 15 minutes (to catch reports that just finished)
                        const timeSinceStart = (Date.now() - startTime) / 1000 / 60; // minutes
                        const timeSinceReport = (Date.now() - reportTimestamp) / 1000 / 60; // minutes
                        
                        console.log('Polling check:', {
                            reportTime: latestDirectReport.generated_at,
                            reportTimestamp,
                            startTime,
                            timeSinceStart: timeSinceStart.toFixed(2),
                            timeSinceReport: timeSinceReport.toFixed(2),
                            condition1: reportTimestamp > (startTime - 5 * 60 * 1000),
                            condition2: timeSinceReport < 15,
                            pollCount
                        });
                        
                        // Report is ours if:
                        // 1. It was generated after we started (with 5 min buffer), OR
                        // 2. It was generated within the last 15 minutes (regardless of how long we've been polling), OR
                        // 3. It's the most recent direct report and we've been polling (fallback for edge cases), OR
                        // 4. If we've been polling for a while and found a recent report, assume it's ours (stop infinite polling)
                        const isOurReport = (reportTimestamp > (startTime - 5 * 60 * 1000)) || 
                                          (timeSinceReport < 15) ||
                                          (pollCount > 1 && timeSinceReport < 30) || // Fallback: if we've polled at least twice and report is recent
                                          (pollCount > 5 && timeSinceReport < 60); // Stop polling if we've checked multiple times and found a recent report
                        
                        console.log('Is our report?', isOurReport);
                        
                        if (isOurReport) {
                            console.log('✅ Configuration Audit completed! Found report:', latestDirectReport.filename);
                            console.log('Report details:', {
                                id: latestDirectReport.id,
                                audit_history_id: latestDirectReport.audit_history_id,
                                generated_at: latestDirectReport.generated_at,
                                report_type: latestDirectReport.report_type,
                                timeSinceReport: timeSinceReport.toFixed(2) + ' minutes',
                                timeSinceStart: timeSinceStart.toFixed(2) + ' minutes'
                            });
                            
                            // Stop polling
                            console.log('🛑 Stopping polling - report found');
                            if (directAuditPollingInterval) {
                                clearInterval(directAuditPollingInterval);
                                setDirectAuditPollingInterval(null);
                                console.log('✅ Polling interval cleared');
                            }
                            
                            // Clear loading state for all firewalls
                            setLoadingAnalysis(false);
                            
                            setSnackbar({
                                open: true,
                                message: 'Configuration Audit completed! Opening PDF report...',
                                severity: 'success'
                            });
                            
                            // Open the PDF report directly using the report ID we found
                            // This ensures we open the correct (new) report, not an old one
                            if (latestDirectReport.id) {
                                console.log('Opening report directly by ID:', latestDirectReport.id);
                                handleOpenReport(latestDirectReport.id, latestDirectReport.filename);
                            } else {
                                // Fallback to the old method if ID is not available
                                console.log('Report ID not available, using fallback method');
                                handleOpenReportInNewTab(firewallId, latestDirectReport.audit_history_id);
                            }
                            
                            // Refresh firewall data
                            fetchFirewalls();
                            
                            return true; // Stop polling
                        } else {
                            // If we found a direct report but it doesn't match our time criteria,
                            // and we've been polling for a while, stop to avoid infinite polling
                            if (pollCount > 10 && timeSinceReport < 120) { // If we've polled 10+ times and report is within 2 hours
                                console.log('⚠️ Found direct report but time conditions not met. Stopping polling to avoid infinite loop.');
                                if (directAuditPollingInterval) {
                                    clearInterval(directAuditPollingInterval);
                                    setDirectAuditPollingInterval(null);
                                }
                                setLoadingAnalysis(false);
                                setSnackbar({
                                    open: true,
                                    message: 'Configuration Audit may have completed. Please check the Reports tab.',
                                    severity: 'info'
                                });
                                return true; // Stop polling
                            }
                        }
                    }
                }
                
                // Continue polling if not found yet
                if (pollCount < maxPolls) {
                    // Poll silently in the background without showing status messages
                    return false; // Continue polling
                } else {
                    // Max polls reached
                    console.log('Max polling attempts reached');
                    if (directAuditPollingInterval) {
                        clearInterval(directAuditPollingInterval);
                        setDirectAuditPollingInterval(null);
                    }
                    // Clear loading state when polling times out
                    setLoadingAnalysis(false);
                    setSnackbar({
                        open: true,
                        message: 'Polling timeout reached. Please check the Reports tab manually.',
                        severity: 'warning'
                    });
                    return true; // Stop polling
                }
            } catch (error) {
                // Silently swallow timeouts — backend is busy processing the audit
                const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout');
                if (!isTimeout) {
                    console.error('Error polling for Configuration Audit completion:', error);
                }
                // Continue polling on error (network issues and timeouts are temporary)
                if (pollCount < maxPolls) {
                    return false; // Continue polling
                } else {
                    if (directAuditPollingInterval) {
                        clearInterval(directAuditPollingInterval);
                        setDirectAuditPollingInterval(null);
                    }
                    // Clear loading state when polling fails after max attempts
                    setLoadingAnalysis(false);
                    return true; // Stop polling
                }
            }
        };
        
        // Start polling immediately (fire and forget - don't await)
        pollForCompletion().then((initialResult) => {
            if (initialResult) {
                // Already found, no need to set up interval
                console.log('✅ Report found on initial poll, skipping interval setup');
                return;
            }
        }).catch((error) => {
            console.error('Error in initial poll:', error);
        });
        
        // Set up interval for subsequent polls
        const interval = setInterval(async () => {
            try {
                const shouldStop = await pollForCompletion();
                if (shouldStop) {
                    console.log('🛑 Stopping polling interval - shouldStop=true');
                    clearInterval(interval);
                    setDirectAuditPollingInterval(null);
                    // Remove from active intervals
                    activeIntervalsRef.current = activeIntervalsRef.current.filter(id => id !== interval);
                    console.log('✅ Polling stopped - audit completed or timeout reached');
                }
            } catch (error) {
                console.error('Error in polling interval:', error);
                // On error, stop polling to prevent infinite loops
                clearInterval(interval);
                setDirectAuditPollingInterval(null);
                activeIntervalsRef.current = activeIntervalsRef.current.filter(id => id !== interval);
            }
        }, pollInterval);
        
        // Track this interval
        activeIntervalsRef.current.push(interval);
        setDirectAuditPollingInterval(interval);
        console.log(`🔄 Started polling for firewall ${firewallId}, will check every ${pollInterval/1000} seconds, max ${maxPolls} attempts`);
        
        // Store interval ID in a way that can be accessed for cleanup
        // Also set a timeout to force stop after max time
        const maxPollingTime = maxPolls * pollInterval; // Total max time in ms
        const timeoutId = setTimeout(() => {
            if (interval) {
                console.log('⏰ Max polling time reached, forcing stop');
                clearInterval(interval);
                setDirectAuditPollingInterval(null);
                activeIntervalsRef.current = activeIntervalsRef.current.filter(id => id !== interval);
                setLoadingAnalysis(false);
            }
        }, maxPollingTime);
        
        // Store timeout for cleanup
        activeIntervalsRef.current.push(timeoutId);
    };

    const handleRunDirectAnalysis = async () => {
        if (!selectedFirewallForAnalysis) return;

        const startTime = Date.now();
        let pollingStarted = false;

        try {
            setLoadingAnalysis(true);
            console.log('🔍 Starting direct analysis for firewall:', {
                id: selectedFirewallForAnalysis.id,
                name: selectedFirewallForAnalysis.device_name,
                type: selectedFirewallForAnalysis.type
            });
            
            setSnackbar({
                open: true,
                message: 'Starting configuration analysis... This may take up to 10 minutes. Polling for completion in background...',
                severity: 'info'
            });

            // Get the stored configuration
            const configResponse = await firewallService.getStoredConfig(selectedFirewallForAnalysis.id);
            if (!configResponse || !configResponse.config) {
                throw new Error('No configuration found for this firewall. Please fetch the configuration first.');
            }

            // Get the active model
            const activeModel = getActiveModel();
            if (!activeModel) {
                throw new Error('Please select an AI model (offline or online) before analyzing the configuration');
            }
            console.log('🔍 Active model:', activeModel);

            // Local model: unload every other local model and load this one
            // first (instant if it already is the only one loaded). The backend
            // checks again when the audit starts, so any failure other than
            // another audit using a different model is only logged here.
            if (isLocalModelSelected()) {
                setLoadingModelForRun(true);
                try {
                    const load = await firewallService.loadLocalModel(activeModel, 'at_run');
                    setModelLoadState('loaded');
                    if (load.loaded_now) setModelLoadSeconds(load.seconds);
                } catch (loadError) {
                    if (loadError.response?.status === 409) {
                        throw new Error(loadError.response.data.detail);
                    }
                    console.warn('Loading the model before the audit failed, continuing:', loadError);
                } finally {
                    setLoadingModelForRun(false);
                }
            }

            // Start polling in parallel (before the main request)
            // This ensures we catch completion even if the main request times out
            startDirectAuditPolling(selectedFirewallForAnalysis.id, startTime);
            pollingStarted = true;

            // Run the direct analysis
            const result = await firewallService.analyzeConfigDirect(selectedFirewallForAnalysis.id, {
                config: configResponse.config,
                analysis_prompt: analysisPrompt,
                model: activeModel,
                // Sent only for gpt-oss; the backend passes it on as Ollama `think` or MLX `reasoning_effort`.
                ...(isGptOssSelected() ? { reasoning: reasoningLevel } : {}),
                // Off/On thinking switch for MLX and Ollama models that have one.
                ...(isThinkingToggleSelected() ? { reasoning: thinkingMode } : {}),
                skip_correlation: skipCorrelation,
                use_rag: useRag,
                max_sections: 3,
                excluded_sections: excludedSections,
            });
            const endTime = Date.now();
            const executionTime = (endTime - startTime) / 1000; // Convert to seconds
            
            // If we got a result, stop polling (success case)
            if (directAuditPollingInterval) {
                clearInterval(directAuditPollingInterval);
                setDirectAuditPollingInterval(null);
            }

                console.log('📊 Direct Analysis Result:', result);
            console.log('📊 Result.results:', result?.results);
            console.log('📊 Result.results length:', result?.results?.length);
            console.log('📊 Result.audit_id:', result?.audit_id);
            console.log('📊 Full result object keys:', result ? Object.keys(result) : 'result is null');

            // Store direct analysis results
            // Check for results array or fallback to result.result
            if (result && ((result.results && result.results.length > 0) || result.result)) {
                // Use results array if available, otherwise create from result.result
                const aiResponseData = result.results && result.results.length > 0 
                    ? result.results[0] 
                    : {
                        status: "COMPLETED",
                        details: result.result?.analysis || result.result?.details || "Analysis completed",
                        remediation_steps: result.result?.recommendations || []
                    };
                
                const directAnalysisResults = {
                    executionTime,
                    configSize: configResponse.config.length,
                    commandComplexity: 0, // Direct analysis doesn't use commands
                    command: "Direct Configuration Analysis",
                    commandDescription: analysisPrompt || "Direct security analysis of firewall configuration",
                    commandSection: "N/A",
                    commandPage: 0,
                    aiResponse: aiResponseData,
                    totalChecks: result.total_checks || 1,
                    passedChecks: result.passed_checks || 1,
                    failedChecks: result.failed_checks || 0,
                    analysisType: "direct",
                    overallScore: result.overall_score || 100.0,
                    duration: result.duration || executionTime,
                    modelUsed: result.model_used || activeModel
                };
                
                console.log('📊 Created directAnalysisResults:', directAnalysisResults);

                setSnackbar({
                    open: true,
                    message: 'Direct analysis completed successfully! Opening PDF report...',
                    severity: 'success'
                });
                
                // Try to open PDF report in new tab (if available)
                // Use the audit_id from the response for direct audits
                const auditId = result.audit_id || null;
                console.log('📊 Extracted audit_id for Configuration Audit:', auditId);
                if (!auditId) {
                    console.warn('⚠️ No audit_id found in Configuration Audit response. Will use fallback to find latest direct audit.');
                }
                handleOpenReportInNewTab(selectedFirewallForAnalysis.id, auditId);
            } else {
                // Fallback: Try to construct from result structure
                console.log('⚠️ Result structure not matching expected format, attempting to construct from available data');
                
                // Check if we have status: 'success' and can create results manually
                if (result && result.status === 'success') {
                    const fallbackResults = {
                        executionTime,
                        configSize: configResponse.config.length,
                        commandComplexity: 0,
                        command: "Direct Configuration Analysis",
                        commandDescription: analysisPrompt || "Direct security analysis of firewall configuration",
                        commandSection: "N/A",
                        commandPage: 0,
                        aiResponse: result.result?.results?.[0] || {
                            status: "COMPLETED",
                            details: result.result?.analysis || result.message || "Analysis completed",
                            remediation_steps: result.result?.recommendations || []
                        },
                        totalChecks: result.total_checks || 1,
                        passedChecks: result.passed_checks || 1,
                        failedChecks: result.failed_checks || 0,
                        analysisType: "direct",
                        overallScore: result.overall_score || 100.0,
                        duration: result.duration || executionTime,
                        modelUsed: result.model_used || activeModel
                    };
                    
                    setSnackbar({
                        open: true,
                        message: 'Direct analysis completed successfully! Opening PDF report...',
                        severity: 'success'
                    });
                    
                    // Try to open PDF report in new tab (if available)
                    // Use the audit_id from the response for direct audits
                    const auditId = result.audit_id || null;
                    console.log('📊 Extracted audit_id for Configuration Audit (fallback path):', auditId);
                    if (!auditId) {
                        console.warn('⚠️ No audit_id found in Configuration Audit response (fallback path). Will use fallback to find latest direct audit.');
                    }
                    handleOpenReportInNewTab(selectedFirewallForAnalysis.id, auditId);
                } else {
                    // Last resort fallback
            setAnalysisResult(result);
            setSnackbar({
                open: true,
                message: 'Configuration analysis completed successfully! Check the Reports tab for the generated report.',
                severity: 'success'
            });
                }
            }
            
            // Clear loading state - audit completed successfully
            setLoadingAnalysis(false);
            
            // Close the analysis dialog
            setAnalyzeConfigOpen(false);
            setAnalysisResult(null);
            
            // Refresh firewall data to show updated audit history
            await fetchFirewalls();
            
            // Refresh audit reports for the current firewall if Reports dialog is open
            if (selectedReportsFirewall && selectedReportsFirewall.id === selectedFirewallForAnalysis.id) {
                await handleLoadMoreReports(selectedFirewallForAnalysis.id, 1);
            }
            
            // Stop polling if it's still running (success case)
            if (directAuditPollingInterval) {
                clearInterval(directAuditPollingInterval);
                setDirectAuditPollingInterval(null);
            }
        } catch (error) {
            console.error('Error running direct analysis:', error);

            // Stop polling on error
            if (directAuditPollingInterval) {
                clearInterval(directAuditPollingInterval);
                setDirectAuditPollingInterval(null);
            }

            let errorMessage = 'An error occurred during analysis';
            let severity = 'error';

            // Check for quota/rate limit error (429)
            if (error.response?.status === 429) {
                errorMessage = 'Free LLM quota exceeded. Please wait a few minutes and try again, or switch to a different AI model.';
                severity = 'error';
                setLoadingAnalysis(false);
            } else if (error.response?.status === 503) {
                const detail = error.response?.data?.detail || '';
                if (detail.toLowerCase().includes('quota') || detail.toLowerCase().includes('free llm')) {
                    errorMessage = detail || 'Free LLM quota exceeded. Please try a different AI model.';
                } else {
                    errorMessage = detail || 'Service temporarily unavailable. Please try again.';
                }
                severity = 'error';
                setLoadingAnalysis(false);
            } else if (error.code === 'ERR_BAD_RESPONSE' && error.response?.status === 504) {
                errorMessage = 'Analysis timed out. The audit may still be processing in the background. Please check the Reports tab in a few minutes.';
                severity = 'warning';
            } else if (error.code === 'ECONNABORTED' || error.isTimeout || error.code === 'ERR_NETWORK' || error.message?.includes('Network Error')) {
                // Timeout or network error occurred - audit may still be running in background
                errorMessage = error.message || 'Request timed out or network error occurred. Polling for completion in background. The audit may still be processing.';
                severity = 'warning';
                
                // If polling wasn't started yet, start it now
                if (!pollingStarted && selectedFirewallForAnalysis) {
                    startDirectAuditPolling(selectedFirewallForAnalysis.id, startTime);
                    pollingStarted = true;
                }
                
                // Don't set loading to false - keep it true so user knows we're still checking
                // Polling will set it to false when audit completes or max time reached
            } else if (error.message) {
                errorMessage = `Error: ${error.message}`;
            }
            
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: severity
            });
            
            // Only set loading to false if we're not polling
            // (Polling will handle loading state when it completes)
            if (!pollingStarted || (!directAuditPollingInterval && (error.code !== 'ECONNABORTED' && !error.isTimeout && error.code !== 'ERR_NETWORK'))) {
                setLoadingAnalysis(false);
            }
        }
    };


    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate('/firewalls/add')}
                >
                    Add Network Device
                </Button>
            </Box>

            {/* Stats Overview - all 6 cards in one row */}
            <Grid container spacing={1.5} sx={{ mb: 2 }}>
                {/* Total Devices */}
                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', color: 'white', boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: 'center', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{firewalls.length}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>Devices</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {/* Total Types */}
                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ background: 'linear-gradient(135deg, #388e3c 0%, #66bb6a 100%)', color: 'white', boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: 'center', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{new Set(firewalls.map(f => f.type)).size}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>Device Vendors</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {/* AI Vendors */}
                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ background: 'linear-gradient(135deg, #0288d1 0%, #4fc3f7 100%)', color: 'white', boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: 'center', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{aiVendors}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>AI Vendors</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {/* Local LLMs */}
                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ background: 'linear-gradient(135deg, #f57c00 0%, #ffb74d 100%)', color: 'white', boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: 'center', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{models.length}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>Local LLMs</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {/* Paid LLMs */}
                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ background: 'linear-gradient(135deg, #7b1fa2 0%, #ba68c8 100%)', color: 'white', boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: 'center', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{onlineModels.length}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>Paid LLMs</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                {/* Free LLMs */}
                <Grid item xs={6} sm={4} md={2}>
                    <Card sx={{ background: 'linear-gradient(135deg, #00897b 0%, #4db6ac 100%)', color: 'white', boxShadow: 2 }}>
                        <CardContent sx={{ textAlign: 'center', p: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>{freeOnlineModels.length}</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>Free LLMs</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                    Filters
                </Typography>
                {/* Device type and sorting filters */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Box sx={{ minWidth: 200 }}>
                        <TextField
                            label="Device Type"
                            value={
                                selectedDeviceId
                                    ? (firewalls.find(fw => fw.id === selectedDeviceId)?.device_name || 'All')
                                    : (selectedType ? (deviceTypeDisplayNames[selectedType] || selectedType) : 'All')
                            }
                            onClick={(e) => setDeviceMenuAnchor(e.currentTarget)}
                            InputProps={{
                                readOnly: true,
                                endAdornment: <ArrowDropDownIcon sx={{ color: 'action.active' }} />,
                            }}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            sx={{ cursor: 'pointer', '& input': { cursor: 'pointer' } }}
                        />
                        <Menu
                            anchorEl={deviceMenuAnchor}
                            open={Boolean(deviceMenuAnchor)}
                            onClose={handleCloseDeviceMenu}
                            PaperProps={{ sx: { width: 260, mt: 0.5 } }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        >
                            <MenuItem
                                selected={!selectedType && !selectedDeviceId}
                                onClick={() => { setSelectedType(''); setSelectedDeviceId(''); handleCloseDeviceMenu(); }}
                            >
                                All
                            </MenuItem>
                            {sortedDeviceTypes.map((type) => (
                                <MenuItem
                                    key={type}
                                    onMouseEnter={(e) => {
                                        if (deviceSubmenuTimeoutRef.current) clearTimeout(deviceSubmenuTimeoutRef.current);
                                        setDeviceSubmenuAnchor(e.currentTarget);
                                        setActiveDeviceType(type);
                                    }}
                                    onMouseLeave={() => {
                                        deviceSubmenuTimeoutRef.current = setTimeout(() => {
                                            setDeviceSubmenuAnchor(null);
                                            setActiveDeviceType(null);
                                        }, 200);
                                    }}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        bgcolor: activeDeviceType === type ? 'action.hover' : 'transparent'
                                    }}
                                >
                                    <Typography variant="body2">{deviceTypeDisplayNames[type] || type}</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Chip
                                            label={groupedDevices[type].length}
                                            size="small"
                                            sx={{ height: 20, fontSize: '0.7rem', minWidth: 28 }}
                                        />
                                        <KeyboardArrowRightIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                                    </Box>
                                </MenuItem>
                            ))}
                        </Menu>
                        <Popper
                            open={Boolean(deviceSubmenuAnchor) && Boolean(activeDeviceType)}
                            anchorEl={deviceSubmenuAnchor}
                            placement="right-start"
                            sx={{ zIndex: 1301 }}
                        >
                            <Paper
                                elevation={8}
                                sx={{ maxHeight: 300, overflowY: 'auto', minWidth: 220 }}
                                onMouseEnter={() => {
                                    if (deviceSubmenuTimeoutRef.current) clearTimeout(deviceSubmenuTimeoutRef.current);
                                }}
                                onMouseLeave={() => {
                                    deviceSubmenuTimeoutRef.current = setTimeout(() => {
                                        setDeviceSubmenuAnchor(null);
                                        setActiveDeviceType(null);
                                    }, 200);
                                }}
                            >
                                <ListSubheader sx={{ bgcolor: 'background.paper', lineHeight: '32px', fontWeight: 'bold', color: 'text.primary' }}>
                                    {deviceTypeDisplayNames[activeDeviceType] || activeDeviceType}
                                </ListSubheader>
                                <MenuItem
                                    selected={selectedType === activeDeviceType && !selectedDeviceId}
                                    onClick={() => {
                                        setSelectedType(activeDeviceType);
                                        setSelectedDeviceId('');
                                        handleCloseDeviceMenu();
                                    }}
                                >
                                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>All {deviceTypeDisplayNames[activeDeviceType] || activeDeviceType}</Typography>
                                </MenuItem>
                                {activeDeviceType && groupedDevices[activeDeviceType]?.map((fw) => (
                                    <MenuItem
                                        key={fw.id}
                                        selected={selectedDeviceId === fw.id}
                                        onClick={() => {
                                            setSelectedType(activeDeviceType);
                                            setSelectedDeviceId(fw.id);
                                            handleCloseDeviceMenu();
                                        }}
                                    >
                                        <Box>
                                            <Typography variant="body2" noWrap>{fw.device_name}</Typography>
                                            <Typography variant="caption" color="text.secondary">{fw.ip_address}</Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Paper>
                        </Popper>
                    </Box>

                    <FormControl sx={{ minWidth: 160 }}>
                        <InputLabel>Protocol</InputLabel>
                        <Select
                            value={selectedProtocol}
                            onChange={(e) => setSelectedProtocol(e.target.value)}
                            label="Protocol"
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="SSH">SSH</MenuItem>
                            <MenuItem value="API">API</MenuItem>
                        </Select>
                    </FormControl>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Sort By</InputLabel>
                            <Select
                                value={sortOption}
                                onChange={handleSortChange}
                                label="Sort By"
                            >
                                <MenuItem value="name">Device Name</MenuItem>
                                <MenuItem value="ip">IP Address</MenuItem>
                                <MenuItem value="createdAt">Device Added</MenuItem>
                                <MenuItem value="recentAudit">Audit Date</MenuItem>
                                <MenuItem value="configUpdated">Config Date</MenuItem>
                            </Select>
                        </FormControl>
                        <Tooltip title={sortDirection === 'asc' ? 'Ascending' : 'Descending'}>
                            <IconButton
                                size="small"
                                onClick={toggleSortDirection}
                                sx={{ mt: 0.5 }}
                            >
                                {sortDirection === 'asc' ? (
                                    <ArrowUpwardIcon fontSize="small" />
                                ) : (
                                    <ArrowDownwardIcon fontSize="small" />
                                )}
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 500 }}>
                            IPv4 Subnet
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            {[0, 1, 2, 3].map((i) => (
                                <React.Fragment key={i}>
                                    <TextField
                                        inputRef={subnetRefs[i]}
                                        value={subnetOctets[i]}
                                        onChange={(e) => handleOctetChange(i, e.target.value)}
                                        onKeyDown={(e) => handleOctetKeyDown(i, e)}
                                        placeholder="0-255"
                                        size="small"
                                        inputProps={{
                                            maxLength: 3,
                                            style: { textAlign: 'center', width: 48, padding: '6px 2px', fontSize: '0.85rem' }
                                        }}
                                    />
                                    {i < 3 && (
                                        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary', mx: 0.1 }}>.</Typography>
                                    )}
                                </React.Fragment>
                            ))}
                            {subnetOctets.some(o => o !== '' && o !== '0') && (
                                <IconButton size="small" onClick={() => setSubnetOctets(['0', '0', '0', '0'])} sx={{ ml: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>✕</Typography>
                                </IconButton>
                            )}
                        </Box>
                    </Box>
                </Box>


                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Grid container spacing={2}>
                    {sortedFirewalls.map((firewall) => (
                        <Grid item xs={12} md={6} lg={4} key={firewall.id}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" gutterBottom>
                                        {firewall.device_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        IP: {firewall.ip_address}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Username: {'•'.repeat(8)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Type: {firewall.type === 'juniper_srx' ? 'Juniper SRX' : 
                                               firewall.type === 'huawei_usg' ? 'Huawei USG' : 
                                               firewall.type === 'fortinet_fortigate' ? 'Fortinet FortiGate' : 
                                               firewall.type === 'cisco_ftd' ? 'Cisco FTD' : 
                                               firewall.type === 'paloalto_panos' ? 'PaloAlto PAN-OS' : 
                                               firewall.type === 'checkpoint_security_gateway' ? 'Checkpoint Security Gateway' : firewall.type}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Protocol: {(firewall.protocol || 'SSH').toUpperCase()}
                                    </Typography>
                                    {(firewall.type === 'juniper_srx' || firewall.type === 'huawei_usg' || firewall.type === 'fortinet_fortigate' || firewall.type === 'cisco_ftd' || firewall.type === 'paloalto_panos' || firewall.type === 'checkpoint_security_gateway') && firewall.config_updated_at && (
                                        <Typography variant="body2" color="text.secondary">
                                            Config Updated: {formatDate(firewall.config_updated_at)}
                                        </Typography>
                                    )}
                                    <Typography variant="body2" color="text.secondary">
                                        Added: {formatDate(firewall.created_at)}
                                    </Typography>
                                    {lastAuditDates[firewall.id] && (
                                        <Typography variant="body2" color="text.secondary">
                                            Last Audit: {formatDate(lastAuditDates[firewall.id])}
                                        </Typography>
                                    )}
                                    
                                    {/* Audit Progress Bar */}
                                    {(auditProgress[firewall.id] || runningAuditId === firewall.id) && (
                                        <Box sx={{ mt: 2 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {auditProgress[firewall.id]?.message || 'Starting audit...'}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {Math.max(0, Math.min(100, auditProgress[firewall.id]?.progress || 0))}%
                                                </Typography>
                                            </Box>
                                            <LinearProgress 
                                                variant={auditProgress[firewall.id] ? "determinate" : "indeterminate"}
                                                value={Math.max(0, Math.min(100, auditProgress[firewall.id]?.progress || 0))}
                                                sx={{ 
                                                    height: 6,
                                                    borderRadius: 3,
                                                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                                                    '& .MuiLinearProgress-bar': {
                                                        borderRadius: 3,
                                                    }
                                                }}
                                            />
                                            {auditProgress[firewall.id]?.current_check && (
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                    Check: {auditProgress[firewall.id].current_check}
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </CardContent>
                                <CardActions sx={{ justifyContent: 'center', px: 0.25, pb: 0.25, gap: 0.1, flexWrap: 'nowrap', overflow: 'auto' }}>
                                        <Tooltip title="Edit Firewall">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleEditClick(firewall)}
                                                color="primary"
                                                disabled={processingAction}
                                            sx={{ minWidth: 'auto', padding: '2px' }}
                                            >
                                            <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Delete Firewall">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDeleteClick(firewall)}
                                                color="error"
                                                disabled={processingAction}
                                            sx={{ minWidth: 'auto', padding: '2px' }}
                                            >
                                            <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        {(firewall.type === 'juniper_srx' || firewall.type === 'huawei_usg' || firewall.type === 'fortinet_fortigate' || firewall.type === 'cisco_ftd' || firewall.type === 'paloalto_panos' || firewall.type === 'checkpoint_security_gateway') && (
                                            <>
                                            <Tooltip title="Stored Configuration">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleViewStoredConfig(firewall)}
                                                        disabled={processingAction || loadingStoredConfig}
                                                    sx={{ minWidth: 'auto', padding: '2px' }}
                                                    >
                                                    <VisibilityIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Fetch Fresh Configuration">
                                                    <IconButton
                                                        size="small"
                                                        color="info"
                                                        onClick={() => handleViewConfig(firewall)}
                                                        disabled={processingAction || viewingConfig}
                                                    sx={{ minWidth: 'auto', padding: '2px' }}
                                                    >
                                                    <HistoryIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    <Tooltip title="View Last Audit PDF Report">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewLastAudit(firewall)}
                                                disabled={processingAction || loadingLastAudit}
                                            sx={{ minWidth: 'auto', padding: '2px', color: '#ef5350' }}
                                            >
                                            <PictureAsPdfIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    {(runningAuditId === firewall.id || (auditProgress[firewall.id] && ['running', 'starting'].includes(auditProgress[firewall.id].status))) && (
                                            <Tooltip title="Stop Audit">
                                                <IconButton
                                                    size="small"
                                                    color="error"
                                                    onClick={() => {
                                                        if (runningAuditId === firewall.id) {
                                                            handleStopAudit(runningAuditId);
                                                        } else if (auditProgress[firewall.id] && ['running', 'starting'].includes(auditProgress[firewall.id].status)) {
                                                            handleStopAudit(firewall.id);
                                                        }
                                                    }}
                                                    disabled={processingAction}
                                                sx={{ minWidth: 'auto', padding: '2px' }}
                                                >
                                                <StopIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    <Tooltip title="Configuration Audit">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleAnalyzeConfigDirect(firewall)}
                                            disabled={loadingAnalysis}
                                            sx={{ minWidth: 'auto', padding: '2px', color: '#fbbf24' }}
                                        >
                                            {loadingAnalysis ? (
                                                <CircularProgress size={18} color="inherit" />
                                            ) : (
                                                <PlayArrowIcon fontSize="small" />
                                            )}
                                        </IconButton>
                                    </Tooltip>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Paper>

            {/* Edit Dialog */}
            <Dialog
                open={editDialogOpen}
                onClose={() => !processingAction && setEditDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Edit Firewall</DialogTitle>
                <DialogContent>
                    {editError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {editError}
                        </Alert>
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        <TextField
                            label="Device Name"
                            name="device_name"
                            value={editForm.device_name}
                            onChange={handleInputChange}
                            fullWidth
                            required
                            disabled={processingAction}
                        />
                        <TextField
                            label="IP Address"
                            name="ip_address"
                            value={editForm.ip_address}
                            onChange={handleInputChange}
                            fullWidth
                            required
                            disabled={processingAction}
                        />
                        <TextField
                            label="Username"
                            name="username"
                            value={editForm.username}
                            onChange={handleInputChange}
                            fullWidth
                            required
                            disabled={processingAction}
                            helperText="Leave empty to keep current username"
                        />
                        <FormControl fullWidth required>
                            <InputLabel>Device Type</InputLabel>
                            <Select
                                name="type"
                                value={editForm.type}
                                onChange={handleInputChange}
                                label="Device Type"
                                disabled={processingAction}
                            >
                                <MenuItem value="juniper_srx">Juniper SRX</MenuItem>
                                <MenuItem value="huawei_usg">Huawei USG</MenuItem>
                                <MenuItem value="fortinet_fortigate">Fortinet FortiGate</MenuItem>
                                <MenuItem value="cisco_ftd">Cisco FTD</MenuItem>
                                <MenuItem value="paloalto_panos">PaloAlto PAN-OS</MenuItem>
                                <MenuItem value="checkpoint_security_gateway">Checkpoint Security Gateway</MenuItem>
                                <MenuItem value="unknown">Unknown</MenuItem>
                            </Select>
                        </FormControl>
                        {(editForm.type === 'cisco_ftd' || editForm.type === 'juniper_srx' || editForm.type === 'paloalto_panos') && (
                            <FormControl fullWidth>
                                <InputLabel>Protocol</InputLabel>
                                <Select
                                    name="protocol"
                                    value={editForm.protocol || 'SSH'}
                                    onChange={handleInputChange}
                                    label="Protocol"
                                    disabled={processingAction}
                                >
                                    <MenuItem value="SSH">SSH</MenuItem>
                                    <MenuItem value="API">API</MenuItem>
                                </Select>
                                <FormHelperText>
                                    {editForm.type === 'cisco_ftd' ? 'Use API for Cisco FTD REST API (FDM); use SSH for CLI access.' :
                                     editForm.type === 'juniper_srx' ? 'Use API for Juniper SRX RPC API; use SSH for CLI access.' :
                                     'Use API for PaloAlto PAN-OS XML API; use SSH for CLI access.'}
                                </FormHelperText>
                            </FormControl>
                        )}
                        <TextField
                            label="Password"
                            name="password"
                            type="password"
                            value={editForm.password}
                            onChange={handleInputChange}
                            fullWidth
                            helperText="Leave empty to keep current password"
                            disabled={processingAction}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setEditDialogOpen(false)} 
                        disabled={processingAction}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleEditSubmit} 
                        variant="contained"
                        disabled={processingAction}
                        startIcon={processingAction ? <CircularProgress size={20} /> : null}
                    >
                        {processingAction ? 'Saving...' : 'Save Changes'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteConfirmOpen}
                onClose={() => !processingAction && setDeleteConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the firewall "{firewallToDelete?.device_name}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setDeleteConfirmOpen(false)} 
                        disabled={processingAction}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleDeleteConfirm} 
                        color="error" 
                        variant="contained"
                        disabled={processingAction}
                        startIcon={processingAction ? <CircularProgress size={20} /> : null}
                    >
                        {processingAction ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Configuration View Dialog - readable UX: sections, search, copy, wrap */}
            <Dialog
                open={viewConfigOpen}
                onClose={() => { setViewConfigOpen(false); setConfigSearch(''); }}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '80vh',
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <DialogTitle sx={{ pb: 0 }}>Firewall Configuration</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, pt: 1 }}>
                    {viewingConfig ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : selectedConfig ? (
                        <>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}>
                                    <Button size="small" variant={configViewMode === 'structured' ? 'contained' : 'outlined'} onClick={() => setConfigViewMode('structured')}>Structured</Button>
                                    <Button size="small" variant={configViewMode === 'raw' ? 'contained' : 'outlined'} onClick={() => setConfigViewMode('raw')}>Raw</Button>
                                </Box>
                                <TextField
                                    size="small"
                                    placeholder="Search in config..."
                                    value={configSearch}
                                    onChange={(e) => setConfigSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{ minWidth: 200, maxWidth: 320 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={configWrapLines}
                                            onChange={(e) => setConfigWrapLines(e.target.checked)}
                                        />
                                    }
                                    label="Wrap lines"
                                />
                                <Tooltip title="Copy full configuration">
                                    <Button
                                        size="small"
                                        startIcon={<ContentCopyIcon />}
                                        onClick={() => {
                                            navigator.clipboard.writeText(selectedConfig).then(
                                                () => setSnackbar({ open: true, message: 'Configuration copied to clipboard', severity: 'success' }),
                                                () => setSnackbar({ open: true, message: 'Failed to copy', severity: 'error' })
                                            );
                                        }}
                                    >
                                        Copy
                                    </Button>
                                </Tooltip>
                            </Box>
                            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: '#0a0e1a', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                {(() => {
                                    const raw = selectedConfig;
                                    const highlight = (text) => {
                                        if (!configSearch.trim()) return text;
                                        const parts = text.split(new RegExp(`(${configSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
                                        return parts.map((p, i) => i % 2 === 1 ? <mark key={i} style={{ backgroundColor: '#ffeb3b', padding: '0 1px' }}>{p}</mark> : p);
                                    };

                                    // Try Juniper SRX structured format first (JSON object)
                                    const juniperStructured = configViewMode === 'structured' ? parseJuniperSrxStructuredConfig(raw) : null;
                                    if (juniperStructured && juniperStructured.isJuniperSrx && juniperStructured.sections && juniperStructured.sections.length > 0) {
                                        return juniperStructured.sections.map((sec, secIdx) => (
                                            <Accordion key={sec.id} defaultExpanded={juniperStructured.sections.length <= 6} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 }, bgcolor: 'primary.50' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>{sec.numberedTitle || `${secIdx + 1}. ${sec.title}`}</Typography>
                                                    {sec.subsections && <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>({sec.subsections.length} subsection{sec.subsections.length !== 1 ? 's' : ''})</Typography>}
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 0, flexDirection: 'column' }}>
                                                    {sec.subsections && sec.subsections.length > 0 ? (
                                                        sec.subsections.map((sub, subIdx) => (
                                                            <Accordion key={sub.id} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', ml: 2 }}>
                                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, bgcolor: 'background.paper', '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{sub.title}</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails sx={{ p: 1.5, pt: 0, bgcolor: '#111827' }}>
                                                                    <Box component="pre" sx={{ fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Consolas", monospace', fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: configWrapLines ? 'pre-wrap' : 'pre', wordBreak: 'break-word', m: 0 }}>
                                                                        {JSON.stringify(sub.data, null, 2).split('\n').map((line, j) => (
                                                                            <span key={j}>{highlight(line)}{'\n'}</span>
                                                                        ))}
                                                                    </Box>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        ))
                                                    ) : (
                                                        <Box sx={{ p: 1.5, bgcolor: '#111827' }}>
                                                            <Box component="pre" sx={{ fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Consolas", monospace', fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: configWrapLines ? 'pre-wrap' : 'pre', wordBreak: 'break-word', m: 0 }}>
                                                                {JSON.stringify(sec.data, null, 2).split('\n').map((line, j) => (
                                                                    <span key={j}>{highlight(line)}{'\n'}</span>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        ));
                                    }

                                    // Try PaloAlto XML structured format
                                    const paloAltoStructured = configViewMode === 'structured' ? parsePaloAltoStructuredConfig(raw) : null;
                                    if (paloAltoStructured && paloAltoStructured.isPaloAlto && paloAltoStructured.sections && paloAltoStructured.sections.length > 0) {
                                        return paloAltoStructured.sections.map((sec, secIdx) => (
                                            <Accordion key={sec.id} defaultExpanded={paloAltoStructured.sections.length <= 6} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 }, bgcolor: '#fff3e0' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e65100' }}>{sec.numberedTitle || `${secIdx + 1}. ${sec.title}`}</Typography>
                                                    {sec.subsections && <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>({sec.subsections.length} subsection{sec.subsections.length !== 1 ? 's' : ''})</Typography>}
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 0, flexDirection: 'column' }}>
                                                    {sec.subsections && sec.subsections.length > 0 ? (
                                                        sec.subsections.map((sub) => (
                                                            <Accordion key={sub.id} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', ml: 2 }}>
                                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, bgcolor: 'background.paper', '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{sub.title}</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails sx={{ p: 1.5, pt: 0, bgcolor: '#111827' }}>
                                                                    <Box component="pre" sx={{ fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Consolas", monospace', fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: configWrapLines ? 'pre-wrap' : 'pre', wordBreak: 'break-word', m: 0 }}>
                                                                        {sub.xml.split('\n').map((line, j) => (
                                                                            <span key={j}>{highlight(line)}{'\n'}</span>
                                                                        ))}
                                                                    </Box>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        ))
                                                    ) : (
                                                        <Box sx={{ p: 1.5, bgcolor: '#111827' }}>
                                                            <Box component="pre" sx={{ fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Consolas", monospace', fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: configWrapLines ? 'pre-wrap' : 'pre', wordBreak: 'break-word', m: 0 }}>
                                                                {sec.xml.split('\n').map((line, j) => (
                                                                    <span key={j}>{highlight(line)}{'\n'}</span>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        ));
                                    }

                                    // Try Huawei USG VRP structured format (#-delimited)
                                    const huaweiStructured = configViewMode === 'structured' ? parseHuaweiUsgStructuredConfig(raw) : null;
                                    if (huaweiStructured && huaweiStructured.isHuaweiUsg && huaweiStructured.sections && huaweiStructured.sections.length > 0) {
                                        // Pre-compute global line offsets for each section
                                        const rawLines = raw.split('\n');
                                        const sectionOffsets = {};
                                        huaweiStructured.sections.forEach(sec => {
                                            const firstLine = (sec.content || '').split('\n')[0]?.trim();
                                            if (firstLine) {
                                                const idx = rawLines.findIndex(l => l.trim() === firstLine);
                                                sectionOffsets[sec.id] = idx >= 0 ? idx : 0;
                                            } else { sectionOffsets[sec.id] = 0; }
                                        });
                                        return huaweiStructured.sections.map((sec) => (
                                            <Accordion key={sec.id} defaultExpanded={false} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' }, bgcolor: '#1a1a2e' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e53935' }}>{sec.numberedTitle}</Typography>
                                                    <Typography component="span" variant="caption" sx={{ ml: 'auto', mr: 1, color: 'text.secondary' }}>{sec.lineCount} line{sec.lineCount !== 1 ? 's' : ''}</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 0 }}>
                                                    <Box component="pre" sx={{ fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Consolas", monospace', fontSize: '0.8rem', lineHeight: 1.5, whiteSpace: configWrapLines ? 'pre-wrap' : 'pre', wordBreak: configWrapLines ? 'break-word' : 'normal', m: 0, p: 2, bgcolor: '#111827', overflowX: configWrapLines ? 'hidden' : 'auto' }}>
                                                        {sec.content.split('\n').map((line, j) => {
                                                            const globalLine = (sectionOffsets[sec.id] || 0) + j + 1;
                                                            return <span key={j}><span style={{ color: '#6b7280', userSelect: 'none', display: 'inline-block', width: '3.5em', textAlign: 'right', marginRight: '1em', fontSize: '0.7rem' }}>{globalLine}</span>{highlight(line)}{'\n'}</span>;
                                                        })}
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        ));
                                    }

                                    // Try FTD structured format (JSON array)
                                    const ftdStructured = configViewMode === 'structured' ? parseFtdStructuredConfig(raw) : null;
                                    if (ftdStructured && ftdStructured.sections && ftdStructured.sections.length > 0) {
                                        return ftdStructured.sections.map((sec, secIdx) => (
                                            <Accordion key={sec.id} defaultExpanded={ftdStructured.sections.length <= 8} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{sec.numberedTitle || `${secIdx + 1}. ${sec.title}`}</Typography>
                                                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>({sec.items.length} item{sec.items.length !== 1 ? 's' : ''})</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 0, flexDirection: 'column' }}>
                                                    {sec.items.map((item, i) => {
                                                        const d = item.data || item;
                                                        const name = d.name || d.id || (item.action ? `${item.action} #${i + 1}` : `Item ${i + 1}`);
                                                        return (
                                                            <Accordion key={i} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none' }}>
                                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, bgcolor: 'background.paper', '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{name}</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails sx={{ p: 1.5, pt: 0, bgcolor: '#111827' }}>
                                                                    <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: configWrapLines ? 'pre-wrap' : 'pre', wordBreak: 'break-word', m: 0 }}>
                                                                        {JSON.stringify(item, null, 2).split('\n').map((line, j) => (
                                                                            <span key={j}>{highlight(line)}{'\n'}</span>
                                                                        ))}
                                                                    </Box>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        );
                                                    })}
                                                </AccordionDetails>
                                            </Accordion>
                                        ));
                                    }

                                    // Fallback to raw section display
                                    const sectionStrings = raw.includes('--- ') ? raw.split(/\n(?=--- )/).filter(Boolean) : [raw];
                                    // Pre-compute global line offsets
                                    const fallbackRawLines = raw.split('\n');
                                    let fallbackCumOffset = 0;
                                    const fallbackOffsets = sectionStrings.map(section => {
                                        const firstLine = section.split('\n')[0]?.trim();
                                        const foundIdx = firstLine ? fallbackRawLines.findIndex((l, i) => i >= fallbackCumOffset && l.trim() === firstLine) : fallbackCumOffset;
                                        const offset = foundIdx >= 0 ? foundIdx : fallbackCumOffset;
                                        fallbackCumOffset = offset + section.split('\n').length;
                                        return offset;
                                    });
                                    return sectionStrings.map((section, idx) => {
                                        const titleMatch = section.match(/^--- (.+?) ---/);
                                        const title = titleMatch ? titleMatch[1] : (sectionStrings.length > 1 ? `Section ${idx + 1}` : 'Configuration');
                                        const body = titleMatch ? section.slice(section.indexOf('\n') + 1) : section;
                                        const sectionStartLine = fallbackOffsets[idx] + (titleMatch ? 1 : 0);
                                        return (
                                            <Accordion key={idx} defaultExpanded={sectionStrings.length <= 3} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}>
                                                    <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{title}</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 0, pt: 0, pb: 1 }}>
                                                    <Box
                                                        component="pre"
                                                        sx={{
                                                            fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Consolas", monospace',
                                                            fontSize: '0.8125rem',
                                                            lineHeight: 1.5,
                                                            m: 0,
                                                            p: 2,
                                                            whiteSpace: configWrapLines ? 'pre-wrap' : 'pre',
                                                            wordBreak: configWrapLines ? 'break-word' : 'normal',
                                                            overflowX: configWrapLines ? 'hidden' : 'auto'
                                                        }}
                                                    >
                                                        {body.split('\n').map((line, i) => (
                                                            <span key={i}><span style={{ color: '#6b7280', userSelect: 'none', display: 'inline-block', width: '3.5em', textAlign: 'right', marginRight: '1em', fontSize: '0.7rem' }}>{sectionStartLine + i + 1}</span>{highlight(line)}{'\n'}</span>
                                                        ))}
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    });
                                })()}
                            </Box>
                        </>
                    ) : (
                        <Typography color="text.secondary">No configuration to display.</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewConfigOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Audit Results Dialog */}
            <Dialog
                open={viewAuditOpen}
                onClose={() => setViewAuditOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '80vh',
                        maxHeight: '80vh'
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Audit Results</Typography>
                        <Box>
                            <Chip 
                                label={`${auditResults?.passed_checks || 0} Passed`}
                                color="success"
                                sx={{ mr: 1 }}
                            />
                            <Chip 
                                label={`${auditResults?.failed_checks || 0} Failed`}
                                color="error"
                            />
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {auditResults ? (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                                <Typography variant="h6" gutterBottom>
                                    Overall Score: {auditResults.overall_score !== null && auditResults.overall_score !== undefined ? auditResults.overall_score.toFixed(1) + '%' : 'N/A'}
                                </Typography>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={auditResults.overall_score}
                                    sx={{ 
                                        height: 10,
                                        borderRadius: 5,
                                        mb: 1
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    Total Checks: {auditResults.total_checks}
                                </Typography>
                            </Box>
                            
                            <List>
                                {auditResults.results.map((result, index) => (
                                    <React.Fragment key={index}>
                                        <ListItem sx={{ 
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            bgcolor: result.status === 'PASS' ? 'success.light' : 'error.light',
                                            mb: 1,
                                            borderRadius: 1
                                        }}>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 1,
                                                width: '100%',
                                                mb: 1
                                            }}>
                                                <Chip 
                                                    label={result.status}
                                                    color={result.status === 'PASS' ? 'success' : 'error'}
                                                    size="small"
                                                />
                                                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                                    {result.check_id}
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ width: '100%', mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    AI Analysis:
                                                </Typography>
                                                <Paper 
                                                    variant="outlined" 
                                                    sx={{ 
                                                        p: 2,
                                                        bgcolor: 'background.paper',
                                                        maxHeight: '200px',
                                                        overflow: 'auto'
                                                    }}
                                                >
                                                    <Typography variant="body2" component="pre" sx={{ 
                                                        whiteSpace: 'pre-wrap',
                                                        fontFamily: 'inherit'
                                                    }}>
                                                        {result.details}
                                                    </Typography>
                                                </Paper>
                                            </Box>
                                            
                                            {result.remediation_steps && result.remediation_steps.length > 0 && (
                                                <Box sx={{ width: '100%' }}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                        Remediation Steps:
                                                    </Typography>
                                                    <List dense>
                                                        {result.remediation_steps.split('\n').map((step, i) => (
                                                            <ListItem key={i} sx={{ py: 0.5 }}>
                                                                <ListItemText 
                                                                    primary={step}
                                                                    primaryTypographyProps={{
                                                                        variant: 'body2'
                                                                    }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Box>
                                            )}
                                        </ListItem>
                                    </React.Fragment>
                                ))}
                            </List>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewAuditOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Last Audit Results Dialog */}
            <Dialog
                open={viewLastAuditOpen}
                onClose={() => setViewLastAuditOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '80vh',
                        maxHeight: '80vh'
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Last Audit Results</Typography>
                        <Box>
                            <Chip 
                                label={`${lastAuditResults?.passed_checks || 0} Passed`}
                                color="success"
                                sx={{ mr: 1 }}
                            />
                            <Chip 
                                label={`${lastAuditResults?.failed_checks || 0} Failed`}
                                color="error"
                            />
                        </Box>
                    </Box>
                    {lastAuditResults?.results?.[0]?.audit_date && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Audit Date: {formatDate(lastAuditResults.results[0].audit_date)}
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    {loadingLastAudit ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : lastAuditResults ? (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                                <Typography variant="h6" gutterBottom>
                                    Overall Score: {lastAuditResults.overall_score !== null && lastAuditResults.overall_score !== undefined ? lastAuditResults.overall_score.toFixed(1) + '%' : 'N/A'}
                                </Typography>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={lastAuditResults.overall_score}
                                    sx={{ 
                                        height: 10,
                                        borderRadius: 5,
                                        mb: 1
                                    }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                    Total Checks: {lastAuditResults.total_checks}
                                </Typography>
                            </Box>
                            
                            <List>
                                {lastAuditResults.results.map((result, index) => (
                                    <React.Fragment key={index}>
                                        <ListItem sx={{ 
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            bgcolor: result.status === 'PASS' ? 'success.light' : 'error.light',
                                            mb: 1,
                                            borderRadius: 1
                                        }}>
                                            <Box sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center', 
                                                gap: 1,
                                                width: '100%',
                                                mb: 1
                                            }}>
                                                <Chip 
                                                    label={result.status}
                                                    color={result.status === 'PASS' ? 'success' : 'error'}
                                                    size="small"
                                                />
                                                <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
                                                    {result.check_id}
                                                </Typography>
                                            </Box>
                                            
                                            <Box sx={{ width: '100%', mb: 2 }}>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                    Analysis:
                                                </Typography>
                                                <Paper 
                                                    variant="outlined" 
                                                    sx={{ 
                                                        p: 2,
                                                        bgcolor: 'background.paper',
                                                        maxHeight: '200px',
                                                        overflow: 'auto'
                                                    }}
                                                >
                                                    <Typography variant="body2" component="pre" sx={{ 
                                                        whiteSpace: 'pre-wrap',
                                                        fontFamily: 'inherit'
                                                    }}>
                                                        {result.details}
                                                    </Typography>
                                                </Paper>
                                            </Box>
                                            
                                            {result.remediation_steps && (
                                                <Box sx={{ width: '100%' }}>
                                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                                        Remediation Steps:
                                                    </Typography>
                                                    <List dense>
                                                        {result.remediation_steps.split('\n').map((step, i) => (
                                                            <ListItem key={i} sx={{ py: 0.5 }}>
                                                                <ListItemText 
                                                                    primary={step}
                                                                    primaryTypographyProps={{
                                                                        variant: 'body2'
                                                                    }}
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                </Box>
                                            )}
                                        </ListItem>
                                    </React.Fragment>
                                ))}
                            </List>
                        </Box>
                    ) : (
                        <Typography color="text.secondary" align="center">
                            No audit results available
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewLastAuditOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Stored Configuration Dialog - same readable UX as View Config (sections, search, copy, wrap) */}
            <Dialog
                open={viewStoredConfigOpen}
                onClose={() => { setViewStoredConfigOpen(false); setStoredConfigSearch(''); }}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '80vh',
                        maxHeight: '80vh',
                        display: 'flex',
                        flexDirection: 'column'
                    }
                }}
            >
                <DialogTitle sx={{ pb: 0 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography variant="h6">Stored Firewall Configuration</Typography>
                            {storedConfig?.updated_at && (
                                <Typography variant="body2" color="text.secondary">
                                    Last Updated: {formatDate(storedConfig.updated_at)}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', flex: 1, pt: 1 }}>
                    {loadingStoredConfig ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : storedConfig?.config ? (
                        <>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
                                <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}>
                                    <Button size="small" variant={storedConfigViewMode === 'structured' ? 'contained' : 'outlined'} onClick={() => setStoredConfigViewMode('structured')}>Structured</Button>
                                    <Button size="small" variant={storedConfigViewMode === 'raw' ? 'contained' : 'outlined'} onClick={() => setStoredConfigViewMode('raw')}>Raw</Button>
                                </Box>
                                <TextField
                                    size="small"
                                    placeholder="Search in config..."
                                    value={storedConfigSearch}
                                    onChange={(e) => setStoredConfigSearch(e.target.value)}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{ minWidth: 200, maxWidth: 320 }}
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={storedConfigWrapLines}
                                            onChange={(e) => setStoredConfigWrapLines(e.target.checked)}
                                        />
                                    }
                                    label="Wrap lines"
                                />
                                <Tooltip title="Copy full configuration">
                                    <Button
                                        size="small"
                                        startIcon={<ContentCopyIcon />}
                                        onClick={handleCopyStoredConfig}
                                    >
                                        Copy
                                    </Button>
                                </Tooltip>
                            </Box>
                            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', bgcolor: '#0a0e1a', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                {(() => {
                                    const raw = storedConfig.config;
                                    const highlight = (text) => {
                                        if (!storedConfigSearch.trim()) return text;
                                        const parts = text.split(new RegExp(`(${storedConfigSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi'));
                                        return parts.map((p, i) => i % 2 === 1 ? <mark key={i} style={{ backgroundColor: '#ffeb3b', padding: '0 1px' }}>{p}</mark> : p);
                                    };

                                    // Try Juniper SRX structured format first (JSON object)
                                    const juniperStructured = storedConfigViewMode === 'structured' ? parseJuniperSrxStructuredConfig(raw) : null;
                                    if (juniperStructured && juniperStructured.isJuniperSrx && juniperStructured.sections && juniperStructured.sections.length > 0) {
                                        return juniperStructured.sections.map((sec, secIdx) => (
                                            <Accordion key={sec.id} defaultExpanded={juniperStructured.sections.length <= 6} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 }, bgcolor: 'primary.50' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>{sec.numberedTitle || `${secIdx + 1}. ${sec.title}`}</Typography>
                                                    {sec.subsections && <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>({sec.subsections.length} subsection{sec.subsections.length !== 1 ? 's' : ''})</Typography>}
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 0, flexDirection: 'column' }}>
                                                    {sec.subsections && sec.subsections.length > 0 ? (
                                                        sec.subsections.map((sub, subIdx) => (
                                                            <Accordion key={sub.id} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', ml: 2 }}>
                                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, bgcolor: 'background.paper', '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{sub.title}</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails sx={{ p: 1.5, pt: 0, bgcolor: '#111827' }}>
                                                                    <Box component="pre" sx={{ fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Consolas", monospace', fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: storedConfigWrapLines ? 'pre-wrap' : 'pre', wordBreak: 'break-word', m: 0 }}>
                                                                        {JSON.stringify(sub.data, null, 2).split('\n').map((line, j) => (
                                                                            <span key={j}>{highlight(line)}{'\n'}</span>
                                                                        ))}
                                                                    </Box>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        ))
                                                    ) : (
                                                        <Box sx={{ p: 1.5, bgcolor: '#111827' }}>
                                                            <Box component="pre" sx={{ fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Consolas", monospace', fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: storedConfigWrapLines ? 'pre-wrap' : 'pre', wordBreak: 'break-word', m: 0 }}>
                                                                {JSON.stringify(sec.data, null, 2).split('\n').map((line, j) => (
                                                                    <span key={j}>{highlight(line)}{'\n'}</span>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        ));
                                    }

                                    // Try PaloAlto XML structured format
                                    const paloAltoStructured = storedConfigViewMode === 'structured' ? parsePaloAltoStructuredConfig(raw) : null;
                                    if (paloAltoStructured && paloAltoStructured.isPaloAlto && paloAltoStructured.sections && paloAltoStructured.sections.length > 0) {
                                        return paloAltoStructured.sections.map((sec, secIdx) => (
                                            <Accordion key={sec.id} defaultExpanded={paloAltoStructured.sections.length <= 6} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 }, bgcolor: '#fff3e0' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e65100' }}>{sec.numberedTitle || `${secIdx + 1}. ${sec.title}`}</Typography>
                                                    {sec.subsections && <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>({sec.subsections.length} subsection{sec.subsections.length !== 1 ? 's' : ''})</Typography>}
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 0, flexDirection: 'column' }}>
                                                    {sec.subsections && sec.subsections.length > 0 ? (
                                                        sec.subsections.map((sub) => (
                                                            <Accordion key={sub.id} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', ml: 2 }}>
                                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, bgcolor: 'background.paper', '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{sub.title}</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails sx={{ p: 1.5, pt: 0, bgcolor: '#111827' }}>
                                                                    <Box component="pre" sx={{ fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Consolas", monospace', fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: storedConfigWrapLines ? 'pre-wrap' : 'pre', wordBreak: 'break-word', m: 0 }}>
                                                                        {sub.xml.split('\n').map((line, j) => (
                                                                            <span key={j}>{highlight(line)}{'\n'}</span>
                                                                        ))}
                                                                    </Box>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        ))
                                                    ) : (
                                                        <Box sx={{ p: 1.5, bgcolor: '#111827' }}>
                                                            <Box component="pre" sx={{ fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Consolas", monospace', fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: storedConfigWrapLines ? 'pre-wrap' : 'pre', wordBreak: 'break-word', m: 0 }}>
                                                                {sec.xml.split('\n').map((line, j) => (
                                                                    <span key={j}>{highlight(line)}{'\n'}</span>
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        ));
                                    }

                                    // Try Huawei USG VRP structured format (#-delimited)
                                    const huaweiStructured = storedConfigViewMode === 'structured' ? parseHuaweiUsgStructuredConfig(raw) : null;
                                    if (huaweiStructured && huaweiStructured.isHuaweiUsg && huaweiStructured.sections && huaweiStructured.sections.length > 0) {
                                        return huaweiStructured.sections.map((sec) => (
                                            <Accordion key={sec.id} defaultExpanded={false} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' }, bgcolor: '#1a1a2e' }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#e53935' }}>{sec.numberedTitle}</Typography>
                                                    <Typography component="span" variant="caption" sx={{ ml: 'auto', mr: 1, color: 'text.secondary' }}>{sec.lineCount} line{sec.lineCount !== 1 ? 's' : ''}</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 0 }}>
                                                    <Box component="pre" sx={{ fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Consolas", monospace', fontSize: '0.8rem', lineHeight: 1.5, whiteSpace: storedConfigWrapLines ? 'pre-wrap' : 'pre', wordBreak: storedConfigWrapLines ? 'break-word' : 'normal', m: 0, p: 2, bgcolor: '#111827', overflowX: storedConfigWrapLines ? 'hidden' : 'auto' }}>
                                                        {sec.content.split('\n').map((line, j) => (
                                                            <span key={j}><span style={{ color: '#6b7280', userSelect: 'none', display: 'inline-block', width: '3.5em', textAlign: 'right', marginRight: '1em', fontSize: '0.7rem' }}>{j + 1}</span>{highlight(line)}{'\n'}</span>
                                                        ))}
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        ));
                                    }

                                    // Try FTD structured format (JSON array)
                                    const ftdStructured = storedConfigViewMode === 'structured' ? parseFtdStructuredConfig(raw) : null;
                                    if (ftdStructured && ftdStructured.sections && ftdStructured.sections.length > 0) {
                                        return ftdStructured.sections.map((sec, secIdx) => (
                                            <Accordion key={sec.id} defaultExpanded={ftdStructured.sections.length <= 8} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{sec.numberedTitle || `${secIdx + 1}. ${sec.title}`}</Typography>
                                                    <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>({sec.items.length} item{sec.items.length !== 1 ? 's' : ''})</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 0, flexDirection: 'column' }}>
                                                    {sec.items.map((item, i) => {
                                                        const d = item.data || item;
                                                        const name = d.name || d.id || (item.action ? `${item.action} #${i + 1}` : `Item ${i + 1}`);
                                                        return (
                                                            <Accordion key={i} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none' }}>
                                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, bgcolor: 'background.paper', '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{name}</Typography>
                                                                </AccordionSummary>
                                                                <AccordionDetails sx={{ p: 1.5, pt: 0, bgcolor: '#111827' }}>
                                                                    <Box component="pre" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', lineHeight: 1.4, whiteSpace: storedConfigWrapLines ? 'pre-wrap' : 'pre', wordBreak: 'break-word', m: 0 }}>
                                                                        {JSON.stringify(item, null, 2).split('\n').map((line, j) => (
                                                                            <span key={j}>{highlight(line)}{'\n'}</span>
                                                                        ))}
                                                                    </Box>
                                                                </AccordionDetails>
                                                            </Accordion>
                                                        );
                                                    })}
                                                </AccordionDetails>
                                            </Accordion>
                                        ));
                                    }

                                    // Fallback to raw section display
                                    const sectionStrings = raw.includes('--- ') ? raw.split(/\n(?=--- )/).filter(Boolean) : [raw];
                                    // Pre-compute global line offsets
                                    const fallbackRawLines = raw.split('\n');
                                    let fallbackCumOffset = 0;
                                    const fallbackOffsets = sectionStrings.map(section => {
                                        const firstLine = section.split('\n')[0]?.trim();
                                        const foundIdx = firstLine ? fallbackRawLines.findIndex((l, i) => i >= fallbackCumOffset && l.trim() === firstLine) : fallbackCumOffset;
                                        const offset = foundIdx >= 0 ? foundIdx : fallbackCumOffset;
                                        fallbackCumOffset = offset + section.split('\n').length;
                                        return offset;
                                    });
                                    return sectionStrings.map((section, idx) => {
                                        const titleMatch = section.match(/^--- (.+?) ---/);
                                        const title = titleMatch ? titleMatch[1] : (sectionStrings.length > 1 ? `Section ${idx + 1}` : 'Configuration');
                                        const body = titleMatch ? section.slice(section.indexOf('\n') + 1) : section;
                                        const sectionStartLine = fallbackOffsets[idx] + (titleMatch ? 1 : 0);
                                        return (
                                            <Accordion key={idx} defaultExpanded={sectionStrings.length <= 3} disableGutters sx={{ '&:before': { display: 'none' }, boxShadow: 'none', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 48, '& .MuiAccordionSummary-content': { my: 1 } }}>
                                                    <Typography variant="subtitle2" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{title}</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails sx={{ p: 0, pt: 0, pb: 1 }}>
                                                    <Box
                                                        component="pre"
                                                        sx={{
                                                            fontFamily: '"SF Mono", "Monaco", "Inconsolata", "Fira Code", "Consolas", monospace',
                                                            fontSize: '0.8125rem',
                                                            lineHeight: 1.5,
                                                            m: 0,
                                                            p: 2,
                                                            whiteSpace: storedConfigWrapLines ? 'pre-wrap' : 'pre',
                                                            wordBreak: storedConfigWrapLines ? 'break-word' : 'normal',
                                                            overflowX: storedConfigWrapLines ? 'hidden' : 'auto'
                                                        }}
                                                    >
                                                        {body.split('\n').map((line, i) => (
                                                            <span key={i}><span style={{ color: '#6b7280', userSelect: 'none', display: 'inline-block', width: '3.5em', textAlign: 'right', marginRight: '1em', fontSize: '0.7rem' }}>{sectionStartLine + i + 1}</span>{highlight(line)}{'\n'}</span>
                                                        ))}
                                                    </Box>
                                                </AccordionDetails>
                                            </Accordion>
                                        );
                                    });
                                })()}
                            </Box>
                        </>
                    ) : storedConfig && !storedConfig.config ? (
                        <Typography color="text.secondary" align="center">
                            No stored configuration available
                        </Typography>
                    ) : (
                        <Typography color="text.secondary" align="center">
                            No stored configuration available
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    {storedConfig && selectedFirewallForConfig && (
                        <Button
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownloadStoredConfig(selectedFirewallForConfig)}
                            variant="outlined"
                            color="primary"
                        >
                            Download Config
                        </Button>
                    )}
                    <Button onClick={() => setViewStoredConfigOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Audit Reports Dialog */}
            <Dialog
                open={viewReportsOpen}
                onClose={() => setViewReportsOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">
                            Audit Reports - {selectedReportsFirewall?.device_name}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {loadingReports ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : auditReports[selectedReportsFirewall?.id]?.reports.length > 0 ? (
                        <>
                            <List>
                                {auditReports[selectedReportsFirewall?.id].reports.map((report, index) => (
                                    <React.Fragment key={report.id}>
                                        <ListItem>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <PictureAsPdfIcon sx={{ fontSize: 18, color: '#ef5350' }} />
                                                        <Typography variant="body1">
                                                            {report.filename}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box sx={{ mt: 1 }}>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Generated: {formatDate(report.generated_at)}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Size: {(report.file_size / 1024).toFixed(1)} KB
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Type: {report.report_type}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Tooltip title="Download Report">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleDownloadReport(report.id, report.filename)}
                                                    >
                                                        <ContentCopyIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Report">
                                                    <IconButton
                                                        size="small"
                                                        color="error"
                                                        onClick={() => handleDeleteReport(report.id, selectedReportsFirewall.id)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </ListItem>
                                        {index < auditReports[selectedReportsFirewall?.id].reports.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                            
                            {/* Pagination Controls */}
                            {auditReports[selectedReportsFirewall?.id]?.pagination && (
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    p: 2, 
                                    borderTop: 1, 
                                    borderColor: 'divider' 
                                }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Showing {auditReports[selectedReportsFirewall?.id].reports.length} of {auditReports[selectedReportsFirewall?.id].pagination.total_count} reports
                                    </Typography>
                                    
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {auditReports[selectedReportsFirewall?.id].pagination.has_previous && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleLoadMoreReports(selectedReportsFirewall.id, auditReports[selectedReportsFirewall?.id].currentPage - 1)}
                                                disabled={loadingReports}
                                            >
                                                Previous
                                            </Button>
                                        )}
                                        
                                        {auditReports[selectedReportsFirewall?.id].pagination.has_next && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => handleLoadMoreReports(selectedReportsFirewall.id, auditReports[selectedReportsFirewall?.id].currentPage + 1)}
                                                disabled={loadingReports}
                                                endIcon={loadingReports ? <CircularProgress size={16} /> : null}
                                            >
                                                Load More
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <PictureAsPdfIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="body1" color="text.secondary">
                                No audit reports available for this firewall
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                Run an audit to generate a PDF report
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewReportsOpen(false)}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Configuration Audit Dialog */}
            <Dialog
                open={analyzeConfigOpen}
                onClose={() => !loadingAnalysis && setAnalyzeConfigOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '70vh',
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle>
                    <Typography variant="h6">
                        Configuration Audit - {selectedFirewallForAnalysis?.device_name}
                    </Typography>
                </DialogTitle>
                <DialogContent>
                    {!analysisResult ? (
                        <Box sx={{ mt: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                            {/* AI Model Selection */}
                            <Box sx={{ flex: 1, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    Select AI Model *
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <FormControl sx={{ minWidth: 220, maxWidth: 280 }} size="small" disabled={loadingAnalysis || modelLoadState === 'loading'}>
                                        <Select
                                            value=""
                                            open={false}
                                            onOpen={handleVendorMenuOpen}
                                            displayEmpty
                                            sx={{ borderRadius: 2 }}
                                            renderValue={() => {
                                                const info = getSelectedVendorInfo();
                                                if (!info) {
                                                    return (
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <StorefrontIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                                                            <Typography variant="body2" sx={{ opacity: 0.5 }}>
                                                                Select LLM
                                                            </Typography>
                                                        </Box>
                                                    );
                                                }
                                                return (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        {info.vendor.icon ? (
                                                            <Box
                                                                component="img"
                                                                src={info.vendor.icon}
                                                                alt={info.vendor.label}
                                                                sx={{ width: 20, height: 20, borderRadius: '4px', objectFit: 'contain' }}
                                                            />
                                                        ) : (
                                                            <AutoAwesomeIcon sx={{ width: 20, height: 20, color: '#D97706' }} />
                                                        )}
                                                        <Typography variant="body2" noWrap>
                                                            {info.model.displayName}
                                                        </Typography>
                                                    </Box>
                                                );
                                            }}
                                        >
                                            <MenuItem value="" disabled />
                                        </Select>
                                    </FormControl>

                                    {isGptOssSelected() && (
                                        <FormControl sx={{ minWidth: 170 }} size="small" disabled={loadingAnalysis || modelLoadState === 'loading'}>
                                            <InputLabel id="reasoning-level-label">Reasoning</InputLabel>
                                            <Select
                                                labelId="reasoning-level-label"
                                                label="Reasoning"
                                                value={reasoningLevel}
                                                onChange={(e) => setReasoningLevel(e.target.value)}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                <MenuItem value="low">Low (faster)</MenuItem>
                                                <MenuItem value="medium">Medium (default)</MenuItem>
                                                <MenuItem value="high">High (slower)</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}

                                    {isThinkingToggleSelected() && (
                                        <FormControl sx={{ minWidth: 170 }} size="small" disabled={loadingAnalysis || modelLoadState === 'loading'}>
                                            <InputLabel id="thinking-mode-label">Thinking</InputLabel>
                                            <Select
                                                labelId="thinking-mode-label"
                                                label="Thinking"
                                                value={thinkingMode}
                                                onChange={(e) => setThinkingMode(e.target.value)}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                <MenuItem value="off">Off (default)</MenuItem>
                                                <MenuItem value="on">On</MenuItem>
                                            </Select>
                                        </FormControl>
                                    )}

                                    {isLocalModelSelected() && (
                                        <Tooltip
                                            arrow
                                            title="Unloads every other local model (Ollama and LM Studio) and loads this one, so the audit starts without a load."
                                        >
                                            <span>
                                                <Button
                                                    variant={modelLoadState === 'loaded' ? 'contained' : 'outlined'}
                                                    color={modelLoadState === 'loaded' ? 'success' : modelLoadState === 'error' ? 'error' : 'primary'}
                                                    onClick={handleLoadModel}
                                                    disabled={loadingAnalysis || modelLoadState === 'loading'}
                                                    startIcon={
                                                        modelLoadState === 'loading' ? <CircularProgress size={16} color="inherit" />
                                                            : modelLoadState === 'loaded' ? <CheckIcon />
                                                                : null
                                                    }
                                                    sx={{ borderRadius: 2, minHeight: 40, textTransform: 'none', whiteSpace: 'nowrap' }}
                                                >
                                                    {modelLoadState === 'loading' ? 'Loading…'
                                                        : modelLoadState === 'loaded' ? (modelLoadSeconds != null ? `Loaded (${Math.round(modelLoadSeconds)} s)` : 'Loaded')
                                                            : modelLoadState === 'error' ? 'Load failed, Retry'
                                                                : 'Load model'}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    )}

                                    {/* AI Vendor Main Menu */}
                                    <Menu
                                        anchorEl={vendorMenuAnchor}
                                        open={Boolean(vendorMenuAnchor)}
                                        onClose={handleVendorMenuClose}
                                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                        sx={{ '& .MuiPaper-root': { minWidth: 240, mt: 0.5 } }}
                                    >
                                        {vendorGroups.map((vendor) => (
                                            <MenuItem
                                                key={vendor.key}
                                                onMouseEnter={(e) => handleVendorHover(e, vendor.key)}
                                                sx={{
                                                    py: 1.5,
                                                    backgroundColor: activeVendor === vendor.key ? 'action.hover' : 'transparent',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                                                    {vendor.icon ? (
                                                        <Box
                                                            component="img"
                                                            src={vendor.icon}
                                                            alt={vendor.label}
                                                            sx={{ width: 24, height: 24, borderRadius: '4px', objectFit: 'contain' }}
                                                        />
                                                    ) : (
                                                        <AutoAwesomeIcon sx={{ width: 24, height: 24, color: '#D97706' }} />
                                                    )}
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                            {vendor.label}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {vendor.loading ? 'Loading...' : `${vendor.models.length} model${vendor.models.length !== 1 ? 's' : ''}`}
                                                        </Typography>
                                                    </Box>
                                                    <ChevronRightIcon sx={{ fontSize: 18, opacity: 0.5 }} />
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Menu>

                                    {/* AI Vendor Sub Menu - Models */}
                                    <Menu
                                        anchorEl={vendorSubMenuAnchor}
                                        open={Boolean(vendorSubMenuAnchor) && Boolean(activeVendor)}
                                        onClose={handleVendorMenuClose}
                                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                        sx={{
                                            pointerEvents: 'none',
                                            '& .MuiPaper-root': {
                                                pointerEvents: 'auto',
                                                minWidth: 300,
                                                maxHeight: 400,
                                                overflow: 'auto',
                                                ml: 0.5,
                                            }
                                        }}
                                    >
                                        {activeVendor && (() => {
                                            const vendor = vendorGroups.find(v => v.key === activeVendor);
                                            if (!vendor) return null;
                                            if (vendor.loading) {
                                                return (
                                                    <MenuItem disabled>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <CircularProgress size={16} />
                                                            <Typography variant="body2">Loading models...</Typography>
                                                        </Box>
                                                    </MenuItem>
                                                );
                                            }
                                            if (vendor.models.length === 0) {
                                                return (
                                                    <MenuItem disabled>
                                                        <Typography variant="body2" color="text.secondary">No models available</Typography>
                                                    </MenuItem>
                                                );
                                            }
                                            const currentActiveModel = getActiveModel();
                                            return vendor.models.map((model) => (
                                                <MenuItem
                                                    key={model.name}
                                                    onClick={() => handleVendorModelSelect(vendor.key, model.name)}
                                                    selected={model.name === currentActiveModel}
                                                    sx={{ py: 1 }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                                        {vendor.icon ? (
                                                            <Box
                                                                component="img"
                                                                src={vendor.icon}
                                                                alt={vendor.label}
                                                                sx={{ width: 20, height: 20, borderRadius: '4px', objectFit: 'contain' }}
                                                            />
                                                        ) : (
                                                            <AutoAwesomeIcon sx={{ width: 20, height: 20, color: '#D97706' }} />
                                                        )}
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="body2" noWrap>
                                                                {model.displayName}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {model.description}
                                                            </Typography>
                                                        </Box>
                                                        {model.recommended && (
                                                            <Typography variant="caption" color="secondary" sx={{ fontWeight: 'bold' }}>
                                                                Recommended
                                                            </Typography>
                                                        )}
                                                        {model.name === currentActiveModel && (
                                                            <CheckIcon sx={{ fontSize: 16, color: 'primary.main' }} />
                                                        )}
                                                    </Box>
                                                </MenuItem>
                                            ));
                                        })()}
                                    </Menu>
                                </Box>
                            </Box>

                            {/* NIST Reference Method - RAG Only */}
                            <Box sx={{ flex: 1, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                <Typography variant="subtitle2" gutterBottom>
                                    NIST SP 800-53 Reference Method
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        RAG (Semantic Search)
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    ChromaDB vector search for explainable NIST mapping
                                </Typography>
                            </Box>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        Analysis Prompt
                                    </Typography>
                                    {!isPromptEditable && (
                                        <IconButton
                                            size="small"
                                            onClick={() => setIsPromptEditable(true)}
                                            disabled={loadingAnalysis}
                                            sx={{ ml: 1 }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                    )}
                                </Box>
                                <TextField
                                    fullWidth
                                    multiline
                                    rows={6}
                                    value={analysisPrompt}
                                    onChange={(e) => setAnalysisPrompt(e.target.value)}
                                    disabled={loadingAnalysis || !isPromptEditable}
                                    InputProps={{
                                        readOnly: !isPromptEditable,
                                        sx: {
                                            bgcolor: isPromptEditable ? 'background.paper' : 'action.disabledBackground',
                                            cursor: isPromptEditable ? 'text' : 'default'
                                        }
                                    }}
                                />
                                {isPromptEditable && (
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                        <Button
                                            size="small"
                                            onClick={() => setIsPromptEditable(false)}
                                            disabled={loadingAnalysis}
                                        >
                                            Done
                                        </Button>
                                    </Box>
                                )}
                            </Box>

                            {/* Section Selection */}
                            {availableSections.length > 0 && (
                                <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                    {loadingSections ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                            <CircularProgress size={20} />
                                        </Box>
                                    ) : (
                                        <Box>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                                {availableSections.map((section) => (
                                                    <FormControlLabel
                                                        key={section.name}
                                                        control={
                                                            <Checkbox
                                                                checked={!excludedSections.includes(section.name)}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setExcludedSections(prev => prev.filter(name => name !== section.name));
                                                                    } else {
                                                                        setExcludedSections(prev => [...prev, section.name]);
                                                                    }
                                                                }}
                                                                disabled={loadingAnalysis}
                                                            />
                                                        }
                                                        label={
                                                            <Box>
                                                                <Typography variant="body2">
                                                                    {section.name}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    Size: {(section.size / 1024).toFixed(2)} KB
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                ))}
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={!skipCorrelation}
                                                            onChange={(e) => setSkipCorrelation(!e.target.checked)}
                                                            disabled={loadingAnalysis}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="body2">
                                                            Correlation Analysis
                                                        </Typography>
                                                    }
                                                />
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={useRag}
                                                            onChange={(e) => setUseRag(e.target.checked)}
                                                            disabled={loadingAnalysis}
                                                        />
                                                    }
                                                    label={
                                                        <Typography variant="body2">
                                                            NIST RAG Semantic Search
                                                        </Typography>
                                                    }
                                                />
                                            </Box>
                                            {excludedSections.length > 0 && (
                                                <Typography variant="caption" color="warning.main" sx={{ mt: 2, display: 'block' }}>
                                                    {excludedSections.length} section(s) will be excluded from analysis
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Analysis Results
                            </Typography>
                            <Box sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Model Used: {analysisResult.result?.model_used}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Analysis Type: {analysisResult.result?.analysis_type}
                                </Typography>
                            </Box>
                            
                            <TextField
                                fullWidth
                                multiline
                                rows={15}
                                value={analysisResult.result?.analysis || 'No analysis available'}
                                InputProps={{
                                    readOnly: true,
                                    sx: {
                                        fontFamily: 'monospace',
                                        fontSize: '0.875rem'
                                    }
                                }}
                                sx={{
                                    '& .MuiInputBase-root': {
                                        height: '100%'
                                    }
                                }}
                            />
                            
                            {analysisResult.result?.recommendations && analysisResult.result.recommendations.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Recommendations:
                                    </Typography>
                                    <List dense>
                                        {analysisResult.result.recommendations.map((rec, index) => (
                                            <ListItem key={index} sx={{ py: 0.5 }}>
                                                <ListItemText 
                                                    primary={rec}
                                                    primaryTypographyProps={{
                                                        variant: 'body2'
                                                    }}
                                                />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {!analysisResult ? (
                        <>
                            <Button 
                                onClick={() => setAnalyzeConfigOpen(false)} 
                                disabled={loadingAnalysis}
                            >
                                Cancel
                            </Button>
                            <Button 
                                onClick={handleRunDirectAnalysis} 
                                variant="contained"
                                disabled={loadingAnalysis || !getActiveModel() || modelLoadState === 'loading'}
                                startIcon={loadingAnalysis ? <CircularProgress size={20} /> : null}
                            >
                                {loadingModelForRun ? 'Loading model…' : loadingAnalysis ? 'Analyzing...' : 'Run Analysis'}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button 
                                onClick={() => {
                                    setAnalysisResult(null);
                                    setAnalyzeConfigOpen(false);
                                }}
                            >
                                Close
                            </Button>
                            <Button 
                                onClick={() => setAnalysisResult(null)}
                                variant="outlined"
                            >
                                New Analysis
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default FirewallList; 
